/**
 * The figure — a Mixamo-rigged FBX character (Josh) with a swappable animation
 * clip stream, all on one shared `AnimationMixer`.
 *
 * ═══ Mesh + clip pipeline ═══
 *
 *  - Josh's character mesh ships from Mixamo as a single `.fbx` containing the
 *    skinned mesh + the standard `mixamorig:*` skeleton, posed in T-pose.
 *  - Each animation lives in its own `.fbx`, exported "without skin" — i.e.
 *    just the same `mixamorig:*` skeleton plus animation tracks. Mixamo names
 *    every joint identically across every export, so three's
 *    `AnimationMixer.PropertyBinding.findNode` binds tracks to the live
 *    skeleton **by joint name** with no retargeting math required.
 *
 * ═══ Crossfade ═══
 *
 *  - One mixer, persistent for the lifetime of the figure.
 *  - `playClipFromUrl(url, fadeSec)`: fetches/caches the FBX, materialises an
 *    `AnimationAction` for its first clip, calls `crossFadeFrom` against the
 *    previously playing action so the figure transitions smoothly between
 *    poses rather than hard-cutting.
 *
 * ═══ In-place playback ═══
 *
 * Mixamo clips are authored with root translation baked into `mixamorig:Hips.position`.
 * For a clip-browser UX we want the figure to stay anchored, so by default we
 * **strip the position track on the root** while leaving rotation and the rest of
 * the skeleton untouched. The vertical component of jumps is preserved by the
 * Hips' rotation pulling the body up via the bind hierarchy.
 *
 * ═══ Rendering ═══
 *
 * The FBX ships with its own materials (Mixamo-baked Phong with embedded
 * skin/clothing textures). Two modes, gated by `BODY_TEXTURED`:
 *
 *   - `BODY_TEXTURED = true` — native FBX materials kept as-is. Skin,
 *     clothing, hair textured as Mixamo authored.
 *   - `BODY_TEXTURED = false` (current) — every `SkinnedMesh` is rendered
 *     twice: once with a translucent fill, once as a wireframe overlay.
 *     This is the project's heritage "Body Field" look (see `DESIGN.md`):
 *     ink-grey lines carve the figure's form, a faint warm fill gives it
 *     just enough mass to read as volumetric while breathing.
 *
 *     Implementation: for each `SkinnedMesh` we keep the original (now
 *     painted with the fill material) and add a sibling `SkinnedMesh`
 *     that shares the source's skeleton (`.bind(source.skeleton, …)`) so
 *     it deforms in lockstep without any extra animation work. The
 *     wireframe sibling has `renderOrder + 1` and the fill has
 *     `polygonOffset` so the wires don't z-fight.
 *
 * The original textures aren't deleted: flipping `BODY_TEXTURED` back to
 * `true` and reloading the page reinstates them from the FBX. Frustum
 * culling is disabled on every skinned mesh in either mode because
 * skinned bounding boxes occasionally report empty after large poses,
 * which causes the figure to vanish mid-animation.
 *
 * Lights live in `main.ts`. They affect `BODY_TEXTURED = true` (Phong
 * shaded), but not the `BODY_TEXTURED = false` mode — both fill and wire
 * use `MeshBasicMaterial` so the look stays graphic and crisp regardless
 * of body angle.
 *
 * ═══ Breathing layer ═══
 *
 * The breathing layer is a single additive `AnimationAction` running on the
 * shared mixer. It is controlled by two independent axes that map directly
 * onto how a real body modulates breath:
 *
 *   - `bpm` — breaths per minute. Mapped to `action.timeScale` via
 *     `bpm / INTRINSIC_BPM` (Mixamo's "Standing Idle" loop is ~4 seconds,
 *     ≈ 15 BPM at timeScale 1.0). Faster cadence reads as anxious or
 *     winded; slower as calm or meditative.
 *   - `amplitude` — depth on 0..1, applied directly as the action's
 *     effective weight. Shallow + fast = nervous; deep + slow = relaxed;
 *     deep + fast = exerted. One axis would only let us hit one of the
 *     three states.
 *
 * `setBreathing({ url?, bpm?, amplitude? })` is the public entry point.
 * The update loop interpolates the live state toward the target with a
 * fixed time-constant (`BREATH_SMOOTH_SEC`) so step changes don't snap;
 * a beat that switches breath envelope eases over ~1.5s.
 *
 * For non-breathing additive layers (head-look, weight-shift, etc.) use
 * `playAdditiveClipFromUrl` directly — `setBreathing` is specifically the
 * breathing slot.
 */

import {
  AdditiveAnimationBlendMode,
  AnimationAction,
  AnimationClip,
  AnimationMixer,
  AnimationUtils,
  Color,
  Group,
  LoopOnce,
  LoopPingPong,
  LoopRepeat,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Quaternion,
  SkinnedMesh,
  Vector3,
} from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  ALL_BONE_SLOTS,
  BODY_GESTURE_PROFILES,
  BODY_STATE_PROFILES,
  BONE_PATTERNS,
  LIMB_TO_BONES,
  PROCEDURAL_HOLD_LIMBS,
  PROCEDURAL_HOLD_POSES,
  accumulatePose,
  conflictingHolds,
  emptyOffsets,
  isProceduralHold,
} from './body-profiles';
import type {
  BodyGesture,
  BodyState,
  BoneSlot,
  EulerOffset,
  ProceduralHoldName,
} from './body-profiles';
import { EASING, type EasingFamily } from './easing';
import { type RigProfile } from './rig-profiles';
import {
  setupCcRetargeter,
  processClipForCcRig,
  HIP_POSITION_TRACK_RE,
  type CcRetargetContext,
} from './cc-retarget';
import {
  fixCcGlbLayeringMaterials,
  fixCcTransparentMaterials,
  hideBodySkinSlots,
  stripBlenderStudioExtras,
  type CcSkinSlot,
} from './cc-glb-materials';
import {
  expandCcBaseVisemeWeights,
  isCcBaseLipMorphName,
  CC_JAW_BONE_NAME_PATTERN,
  CC_JAW_OPEN_AXIS,
  CC_JAW_OPEN_RADIANS,
} from './cc-face-rig';
import {
  buildMorphAliasIndex,
  LIP_SYNC_BLENDSHAPES,
  type MorphAliasIndex,
} from './morph-aliases';

/**
 * When `false`, native FBX textures are bypassed at mesh-material
 * assignment time and replaced with the wireframe + fill rendering
 * described in the docstring above. The textures themselves are not
 * deleted: flipping this back to `true` and reloading the page
 * reinstates them as authored.
 */
const BODY_TEXTURED = true;

/** Ink-grey lines carving the figure's form. Matches the body text colour for consistency. */
const WIRE_COLOR = '#23272e';
/**
 * Wireframe alpha. Lowered from "firm 0.85" so the fill reads as the
 * primary mass and the wires read as a graphic overlay on top — important
 * for high-poly Mixamo meshes (Josh has ~15k tris) where dense wires
 * otherwise blend into a solid black blob.
 */
const WIRE_OPACITY = 0.55;
/** Warm medium-grey — clearly visible against the `#fafaf7` background. */
const FILL_COLOR = '#b8b3a9';
/**
 * Fill alpha. Bumped from 0.55 → 0.85 so the figure reads as a solid
 * mass against the background instead of a ghost — easier to track
 * during fast motion (locomotion, gestures) and less visually
 * competitive with the wireframe overlay. Going fully opaque (1.0)
 * loses the "graphic mannequin" feel; 0.85 keeps a touch of see-through
 * at silhouette edges.
 */
const FILL_OPACITY = 0.85;

export interface LoadFigureOpts {
  /** Hide nodes whose name matches any pattern (kept for parity with prior loader). */
  hideNamePatterns?: RegExp[];
  /**
   * Mixamo FBX exports are in centimetres; the rest of the scene is in metres.
   * Default `0.01` rescales the figure to a sensible size. Pass `1` to keep cm.
   */
  scale?: number;
  /**
   * Target-rig profile for clip-track binding.
   *
   * HY-Motion / Mixamo / DeepMotion clips all ship with Mixamo-named bone
   * tracks (`mixamorigHips.quaternion`, …). Mixamo / Avaturn / RPM characters
   * have those exact bone names so binding works directly. cc-base (Character
   * Creator 4) characters have bones named `CC_Base_Hip`, `CC_Base_L_Upperarm`
   * etc., AND have a different bind pose, so a name remap alone isn't
   * enough — applied rotations would deform the character.
   *
   * When set to a non-Mixamo profile, `loadFigure` builds an
   * `AnimationRetargeting` instance (using Lewis as the source-skeleton
   * donor) and every raw clip runs through `processClipForCcRig`:
   * bind-pose-aware quaternion retargeting plus a world-space hip
   * translation. See `cc-retarget.ts` for full rationale.
   *
   * Omit (or pass `'mixamo'` / `'avaturn'`) for Mixamo-rigged characters
   * — they need no retargeting and the clip is bound as-is.
   */
  rigProfile?: RigProfile;
  /**
   * Hide one or more CC body-skin slots after load, so any residual
   * pose-driven clothing skinning gaps (after weight-transfer
   * remediation) don't expose skin through the seam. See
   * `hideBodySkinSlots` in `cc-glb-materials.ts` for mechanism and
   * per-slot coverage caveats.
   *
   * Only meaningful when `rigProfile === 'cc-base'` /
   * `'cc-base-noprefix'`; ignored otherwise. Per-character in the
   * manifest:
   *   - Riya: `['body']`  (high-collar shirt fully covers torso)
   *   - Anne: `['leg']`   (full-length jeans + heels fully cover legs)
   */
  hideSkinSlots?: CcSkinSlot[];
}

export interface ClipManifestEntry {
  name: string;
  url: string;
}

/**
 * Two-axis breathing state. See the "Breathing layer" docstring above for
 * the physiological mapping. Both fields are clamped on input to safe
 * ranges (`BPM_MIN`/`BPM_MAX`, `AMP_MIN`/`AMP_MAX`).
 */
export interface BreathState {
  /** Breaths per minute. Typical ranges: 6–10 calm, 12–18 neutral, 20–30 tense, 30+ winded. */
  bpm: number;
  /** Depth, 0..1, applied directly as the additive action's weight. */
  amplitude: number;
}

export interface SetBreathingOpts extends Partial<BreathState> {
  /**
   * If provided and different from the currently loaded breathing clip,
   * swaps to the new clip. The first `setBreathing` call must include a
   * `url` to start the layer.
   */
  url?: string;
}

export interface Figure {
  root: Group;
  /** Advance whatever is currently playing on the mixer. Call once per frame. */
  update: (dtSec: number) => void;
  /** Time-scale modulator on the main action (additive layers are unaffected). */
  setTimeScale: (s: number) => void;
  /**
   * Load the FBX at `url`, extract its first `AnimationClip`, and crossfade
   * from the currently playing action over `fadeSec` seconds. First call
   * starts immediately (no fade — there is nothing to fade from).
   *
   * `loop` controls how the clip plays:
   *   - `true` (default) → LoopRepeat
   *   - `false`          → LoopOnce + clampWhenFinished (hold last frame)
   *   - `'pingpong'`     → LoopPingPong (forward, reverse, forward, …)
   *                         Use this for idle variants so they don't read
   *                         as obvious loops.
   *
   * `lockInPlace` controls root motion:
   *   - `true` (default) → strip the `Hips.position` track. The figure
   *                         plays on a treadmill, anchored at its root.
   *                         Used for every face_only / library beat.
   *   - `false`          → keep the hip translation. The figure walks
   *                         through world space; camera follow in
   *                         main.ts smooth-tracks the figure's hip
   *                         world position. Used for uthana_locomotion.
   *
   * `preserveHip` keeps the (frame-0-anchored) `Hips.position` track
   * intact for HY-Motion gestures. Distinct from `lockInPlace=false`:
   *   - locomotion path (`lockInPlace=false`) runs `prepareLocomotionClip`
   *     which rebases relative to bind AND optionally stitches strides.
   *     Used for uthana_locomotion.
   *   - preserve-hip path keeps the raw track AS-IS (after the frame-0
   *     anchor applied during clip load), so HY-Motion lean / reach /
   *     crouch read correctly without forcing the figure into the
   *     locomotion stitching code path. Used for uthana_text.
   */
  playClipFromUrl: (
    url: string,
    fadeSec?: number,
    loop?: boolean | 'pingpong',
    lockInPlace?: boolean,
    strides?: number,
    preserveHip?: boolean,
  ) => Promise<void>;
  /**
   * Queue a clip to play at the next natural transition point of the
   * currently playing action:
   *   - If the current action is looping, the queued clip crossfades in at
   *     the next loop boundary (the `loop` mixer event).
   *   - If the current action is a one-shot, the queued clip starts when it
   *     finishes (the `finished` mixer event).
   *   - If nothing is currently playing, the clip starts immediately.
   *   - Calling again before the queue fires replaces the pending entry
   *     (last call wins).
   *
   * The clip FBX is prefetched immediately on call so the crossfade at the
   * transition point is seamless.
   */
  queueClipFromUrl: (url: string) => void;
  /**
   * Generic additive layer. Loads the FBX at `url`, converts the clip to
   * additive (delta from frame 0), and plays it as a persistent layer at
   * the given weight. Subsequent calls with the same URL just update the
   * weight; different URLs stack additional layers.
   *
   * For breathing specifically, prefer `setBreathing` — it owns a
   * dedicated slot with bpm/amplitude smoothing.
   */
  playAdditiveClipFromUrl: (
    url: string,
    weight?: number,
    opts?: {
      /**
       * Drop tracks whose name matches this regex from the clip before it
       * becomes additive. Used by the breathing layer to exclude arm-chain
       * tracks so held poses don't sway. The cache is keyed on `url +
       * filter signature` so filtered and unfiltered preps don't collide.
       */
      dropTracksMatching?: RegExp;
    },
  ) => Promise<AnimationAction>;
  /**
   * Set or update the breathing layer.
   *
   *   - First call: must include `url`, optionally `bpm` and `amplitude`
   *     (defaults: 15 BPM, 0.4 amplitude).
   *   - Subsequent calls without `url`: just nudge the targets; the live
   *     state interpolates over ~1.5s.
   *   - Subsequent calls with a different `url`: swap the breathing clip
   *     (stops old action, starts new with current targets).
   */
  setBreathing: (opts: SetBreathingOpts) => Promise<void>;
  /** Read the live breathing state (interpolated, not target). For HUDs. */
  getBreathingState: () => BreathState;
  /**
   * Register a callback that fires every time a new main-action clip starts
   * playing (both immediate and queued). Useful for clearing "queued" HUD
   * indicators. Pass `null` to remove.
   */
  setOnClipStart: (cb: ((url: string) => void) | null) => void;
  /**
   * Register a callback that fires when the current non-looping (LoopOnce)
   * action plays through to completion. The beat player uses this to know
   * when to advance to the next beat. Pass `null` to remove.
   *
   * Note: only fires for clips played with `loop = false`. Looping clips
   * fire the `loop` mixer event instead, but the beat player uses a timer
   * for those — it does not use this callback for looping beats.
   */
  setOnClipFinished: (cb: (() => void) | null) => void;
  /**
   * Return the duration (in seconds) of the cached clip at `url`, or
   * `null` if that URL hasn't been loaded yet. Useful for estimating
   * one-shot beat length before the `finished` event fires.
   */
  getClipDuration: (url: string) => number | null;
  /**
   * Stop the current main action, leaving the figure in T-pose. Additive layers continue.
   */
  stop: () => void;
  /**
   * Activate a named additive hold pose layer.
   *
   * Two backends, dispatched by name:
   *   - PROCEDURAL: if `name` is in PROCEDURAL_HOLD_POSES, the runtime
   *     applies a hand-authored per-bone pose with attack/sustain/release
   *     envelope. `url` is ignored. Free, instant, deterministic.
   *   - GLB ADDITIVE: otherwise the runtime loads the GLB at `url` and
   *     plays it as a looping additive layer. Legacy / future Uthana
   *     holds use this path.
   *
   * If the hold is already active, the weight is updated. Use
   * `releaseHold(name)` to fade out and remove.
   */
  /**
   * `attackSec` overrides the default activation fade-in (procedural:
   * ~0.55s, GLB: 0.5s). Only applied on the first activation of a hold;
   * re-issuing setHold for an already-active hold ignores attackSec and
   * just refreshes the weight.
   */
  setHold: (
    name: string,
    url: string,
    weight?: number,
    attackSec?: number,
    easing?: import('./easing').EasingFamily,
  ) => Promise<void>;
  /**
   * Fade out and stop an active named hold. No-op if the hold is not active.
   * `easing` overrides the family used for the release curve; falls back to
   * whatever family was active on the hold.
   */
  releaseHold: (
    name: string,
    fadeSec?: number,
    easing?: import('./easing').EasingFamily,
  ) => void;
  /**
   * Release all active hold layers. Called automatically on teardown.
   */
  releaseAllHolds: (fadeSec?: number) => void;
  /**
   * Beat-driven facial expression layer. Values 0–1 by ARKit shape name.
   * Pass `{}` to clear (the ambient baseline will show through).
   * Only has visible effect on characters with morph targets.
   *
   * Example:
   *   figure.setExpression({ mouthSmileLeft: 0.8, mouthSmileRight: 0.8 })
   */
  setExpression: (weights: Record<string, number>) => void;
  /**
   * Update the emotion-driven eye / brow profile. Called once per beat by
   * the BeatPlayer. Adjusts blink interval/hold, saccade rate/amplitude/
   * down-bias, and brow micro-twitch flavour mix.
   *
   * - emotion: lookup key into the per-emotion profile table (falls back
   *   to neutral if unknown).
   * - intensity: 0..1, how far to lerp from neutral toward the target
   *   profile. 0 = neutral rates; 1 = full target rates.
   * - energy: 0..1, arousal multiplier on intervals. Higher energy = faster
   *   blinks/saccades regardless of emotion polarity.
   *
   * Schedule timers pick up the new rates on their next tick (so a swap
   * mid-blink completes naturally and the *next* blink uses the new rate).
   */
  setEmotionState: (emotion: string, intensity?: number, energy?: number) => void;
  /**
   * Override the procedural saccades with a director-scripted gaze target.
   * Pass `null` to release the override and let saccades resume.
   *
   * Valid directions: 'forward' | 'down' | 'down_left' | 'down_right' |
   *                   'up' | 'up_left' | 'up_right' | 'left' | 'right' | 'away'
   *
   * The BeatPlayer calls this on the leading edge of each beat (with the
   * value of `beat.gaze`) so director-scripted gaze cues "land" on the
   * right phrase, then releases it (passing `null`) when the next beat
   * doesn't request a gaze override.
   *
   * `intensity` scales the gaze amplitude (defaults to 0.7).
   */
  setGazeOverride: (direction: string | null | undefined, intensity?: number) => void;
  /**
   * Fire a procedural gesture overlay — a short (300 ms – 1.5 s)
   * envelope on top of the current emotion that captures transients
   * the emotion lerp cannot (gasp impact, laugh convulsions, head
   * shake). Names: 'gasp' | 'laugh' | 'chuckle' | 'wince' | 'flinch' |
   * 'head_shake' | 'head_nod' | 'eyebrow_flash' | 'sigh'.
   *
   * The BeatPlayer calls this on the leading edge of each beat (with
   * `beat.gesture`). Pass `null`/`undefined` to no-op (gestures don't
   * need explicit clearing — they auto-clear when their duration
   * expires). Calling while a gesture is in flight restarts from t=0.
   */
  triggerGesture: (name: string | null | undefined) => void;
  /**
   * Fire a procedural body gesture overlay (shoulder_shrug,
   * weight_shift, glance_over_shoulder, etc.). Runs on a slot
   * independent of the face gesture so face + body gestures can
   * layer (e.g. gasp face + recoil_step body simultaneously).
   *
   * Same lifecycle as triggerGesture: auto-clears when its duration
   * expires; re-firing restarts from t=0.
   */
  triggerBodyGesture: (name: string | null | undefined) => void;
  /**
   * Set the sustained ambient body posture mode. Crossfades from
   * the current state to the target over ~1 s. Body state offsets
   * apply continuously (not as a one-shot envelope) and stack
   * additively on top of the mixer-driven body clip.
   *
   * Names: 'neutral_idle' | 'restless_idle' | 'slumped_idle' |
   * 'tense_idle' | 'confident_idle' | 'withdrawn_idle'.
   *
   * Pass `null`/`undefined` or `'neutral_idle'` to clear all bias.
   */
  setBodyState: (name: string | null | undefined) => void;
  /**
   * Always-on baseline expression — survives between beats. Use for a
   * subtle "thinking" or "calm" presence so the face is never blank.
   * Set once at startup; the layered system max-blends ambient with the
   * beat layer and procedural blinks/saccades automatically.
   */
  setAmbient: (weights: Record<string, number>) => void;
  /**
   * Real-time lip-sync layer driven by HeadAudio visemes (passed through
   * `viseme-map.ts` to canonical ARKit weights before reaching here).
   * Max-merged with ambient/beat on lip shapes only — never affects brows
   * or eyes. CC rigs get `expandCcBaseVisemeWeights` applied automatically;
   * other rigs receive the canonical dict unchanged.
   */
  setVisemeWeights: (weights: Record<string, number>) => void;
  /** Names of all available morph targets on this character. Empty for non-blendshape characters. */
  morphTargetNames: string[];
  /**
   * Current (live, post-lerp) time-scale on the main action. Reads the
   * `timeScaleCurrent` value that's updated every frame inside `update()`,
   * not the target — so HUDs see the actual playback rate the character is
   * moving at, including the smooth ramp toward a new target.
   */
  getCurrentTimeScale: () => number;
  /**
   * Name of the currently-firing procedural gesture (one of the `Gesture`
   * literals like `'gasp'`/`'laugh'`), or `null` if no gesture envelope is
   * active. Useful for HUDs that want to highlight the moment a gesture
   * lands. Returns `null` once the gesture's duration has expired.
   */
  getActiveGesture: () => string | null;
  /** Currently-firing body gesture, or `null`. Mirror of getActiveGesture. */
  getActiveBodyGesture: () => string | null;
  /** Currently-active body state (sustained posture). Defaults to 'neutral_idle'. */
  getBodyState: () => string;
  /** Names of currently-active procedural holds (subset of `setHold` calls). */
  getActiveProceduralHolds: () => string[];
  /**
   * Fill `out` with the hip bone's world-space position and return it.
   * Used by the camera follow in main.ts to track the figure as it
   * walks during uthana_locomotion beats. Falls back to the figure
   * root's world position when the hip bone wasn't located on load
   * (graceful degradation: the camera still tracks the body, just less
   * precisely).
   *
   * Allocation-free: caller owns the `out` Vector3, so we don't churn
   * the GC on every frame.
   */
  getHipWorldPosition: (out: import('three').Vector3) => import('three').Vector3;
  /**
   * Bind-pose local rotation per BoneSlot, captured at character load
   * before any animation has touched the skeleton. Used by the SMPL
   * retargeter to convert SMPL world-space rotations into Mixamo
   * bone-local Euler offsets — without these, naive retargeting will
   * dump SMPL world-axis rotations directly onto Mixamo bones whose
   * local axes are at arbitrary orientations, producing visibly
   * wrong poses (e.g. arms-up turning into arms-forward).
   *
   * Returns an object keyed by `BoneSlot`. Each entry has both the
   * quaternion (for FK chain composition) and the Euler XYZ form (for
   * the additive layer's offset semantics). Slots that weren't
   * resolved on this rig are absent from the returned object — callers
   * must guard. The returned object is a fresh shallow copy; mutating
   * it does not affect the figure.
   */
  getBindRotations: () => Partial<Record<BoneSlot, {
    qx: number; qy: number; qz: number; qw: number;
    ex: number; ey: number; ez: number;
  }>>;
  /**
   * Stop all actions on the mixer and uncache the root so the mixer can be
   * garbage-collected. Call this before removing the figure from the scene
   * (e.g. when swapping to a different character). The root `Group` is not
   * removed from the scene here — the caller does that.
   */
  teardown: () => void;
}

function hierarchicallyVisible(obj: Object3D): boolean {
  let cur: Object3D | null = obj;
  while (cur !== null) {
    if (!cur.visible) return false;
    cur = cur.parent;
  }
  return true;
}

/**
 * Drop the position track on the root joint so the figure does not travel.
 *
 * The regex matches three rig-naming conventions because this runs on every
 * additive / locked clip, regardless of which pipeline produced it:
 *   - `Hips.position` — Mixamo / HY-Motion bare (post `normalizeMixamoTracks`)
 *   - `mixamorig:Hips.position` / `mixamorigHips.position` — raw Mixamo FBX
 *   - `CC_Base_Hip.position` — cc-base profile output of `processClipForCcRig`
 *   - `Hip.position` — cc-base-noprefix profile output of cc-retarget
 *
 * It is safe to also match `Hip.position` because Mixamo idle/library clips
 * and HY-Motion clips only ever carry a position track on the root joint, and
 * `cc-retarget` explicitly drops non-hip position tracks before re-emitting
 * just the target hip's. So no `LeftHip.position` etc. ever reaches this fn.
 *
 * Bug history: before widening, cc-base characters (Riya/Anne) running the
 * additive Mixamo idle bobbed up/down through the floor — the surviving
 * `CC_Base_Hip.position` track was converted to per-frame deltas by
 * `makeClipAdditive(clip, 0)` and additively shoved the hip vertically on
 * top of whatever the main action was doing.
 */
function lockClipInPlace(clip: AnimationClip): void {
  clip.tracks = clip.tracks.filter((t) => !HIP_POSITION_TRACK_RE.test(t.name));
}

/**
 * Shift the clip's `Hips.position` track so frame-0 == the runtime
 * character's bind hip position, in-place. Subsequent frames preserve
 * their original deltas (jumps / walks / crouches still work) — only the
 * starting offset is corrected.
 *
 * Why this exists (1:1 mirror of cc-retarget.ts step 1, used here for
 * the Mixamo path where we don't go through the upf-gti retargeter):
 *
 * HY-Motion outputs Mixamo-named clips calibrated against an SMPL→Mixamo
 * "neutral" pose whose hip is typically a foot or so above the runtime
 * character's bind hip. Without this shift, the moment a clip starts
 * playing the hip snaps to that higher value and the character floats
 * (the failure mode the previous `preserveHip` attempt produced before
 * the anchor was added — see chat history around messages 6418–6422).
 *
 * Library Mixamo clips (idle / locomotion) get this same anchoring
 * because we run it inside `fetchRawClip`. For library clips that are
 * subsequently locked-in-place (`lockClipInPlace`), the anchor is wasted
 * but harmless. For library locomotion clips, `prepareLocomotionClip`
 * rebases the track itself so the anchor is silently overwritten — also
 * fine. The anchor is only *visible* to HY-Motion gesture clips played
 * via `preserveHip = true` from the beat player.
 *
 * Mutates the clip's track values in place. Operates on the FIRST
 * matching `Hips.position` track found — clips never have more than one.
 */
function anchorHipPositionToBind(
  clip: AnimationClip,
  bindHip: { x: number; y: number; z: number },
): void {
  const hipTrack = clip.tracks.find((t) => HIP_POSITION_TRACK_RE.test(t.name)) as
    | { values: Float32Array; name: string }
    | undefined;
  if (!hipTrack) return;
  const v = hipTrack.values;
  if (v.length < 3) return;
  const f0x = v[0]!;
  const f0y = v[1]!;
  const f0z = v[2]!;
  const dx = bindHip.x - f0x;
  const dy = bindHip.y - f0y;
  const dz = bindHip.z - f0z;
  // No-op when the clip already starts at bind (within numeric noise).
  if (Math.abs(dx) < 1e-5 && Math.abs(dy) < 1e-5 && Math.abs(dz) < 1e-5) return;
  for (let i = 0; i < v.length; i += 3) {
    v[i]     += dx;
    v[i + 1] += dy;
    v[i + 2] += dz;
  }
}

/**
 * Normalise a locomotion clip's hip-position track so it plays cleanly on
 * the figure regardless of the source FBX's units / origin.
 *
 * Two problems this solves:
 *
 *   1. Unit mismatch. Mixamo FBX clips frequently ship with translations
 *      in centimetres (Y ~100, walks of ~50–300 cm). The figure rig is in
 *      metres (hip bind pose Y ~1). Playing the raw track snaps the hip
 *      100× too high and walks the figure across the city. We detect this
 *      via the max delta heuristic (>10 ⇒ cm) and divide by 100.
 *
 *   2. Frame-0 origin offset. Even when units match, the clip's frame-0
 *      hip is rarely at exactly the bind-pose position — Mixamo recordings
 *      start at the mocap rig's anchor, which can be (X=0.5, Y=1.05, Z=0)
 *      instead of the figure's (0, 1.0, 0). When the clip starts, the hip
/**
 * Prepare a locomotion clip for playback by rebasing its hip track to
 * the character's bind-pose hip position and optionally stitching
 * multiple stride cycles end-to-end for multi-stride walks.
 *
 * Single stride (strides=1):
 *   Rebases every frame so the clip starts AT the bind hip and each
 *   frame's value is expressed as a delta from that origin. This means
 *   regardless of where the clip was authored, the figure's hip starts
 *   at its bind position and walks the correct real-world distance.
 *
 * Multiple strides (strides>1):
 *   After rebasing, measures the per-cycle displacement (Δx, Δy, Δz)
 *   as end-frame minus start-frame, then stitches N copies of the
 *   track end-to-end, offsetting each copy by k × Δ. The result is a
 *   single continuous walk track with no teleport on cycle restart.
 *   clip.duration is updated to N × singleCycleDuration.
 *
 *      pops to that offset. We rebase every frame so the clip starts AT
 *      the bind pose and motion is delta-relative.
 *
 * The result: regardless of the source clip, the figure's hip starts at
 * its bind position and walks the correct real-world distance. Vertical
 * bob (the natural up-down of walking) is preserved because deltas are
 * applied to all three axes.
 *
 * Mutates the clip's track values in place.
 */
function prepareLocomotionClip(
  clip: AnimationClip,
  bindHip: { x: number; y: number; z: number },
  strides = 1,
): void {
  const hipTrack = clip.tracks.find((t) => HIP_POSITION_TRACK_RE.test(t.name)) as
    | { values: Float32Array; times: Float32Array }
    | undefined;
  if (!hipTrack) return;
  const v = hipTrack.values;
  if (v.length < 6) return; // need at least 2 frames

  const x0 = v[0]!, y0 = v[1]!, z0 = v[2]!;

  // Compute max delta across all axes to detect the unit scale.
  let maxDelta = 0;
  for (let i = 0; i < v.length; i += 3) {
    const dx = Math.abs(v[i]! - x0);
    const dy = Math.abs(v[i + 1]! - y0);
    const dz = Math.abs(v[i + 2]! - z0);
    if (dx > maxDelta) maxDelta = dx;
    if (dy > maxDelta) maxDelta = dy;
    if (dz > maxDelta) maxDelta = dz;
  }

  // >10 units of delta is implausible for a metres clip (a 1-second
  // walk is ~1 m). Treat as centimetres and convert.
  const unitScale = maxDelta > 10 ? 0.01 : 1.0;

  for (let i = 0; i < v.length; i += 3) {
    v[i] = bindHip.x + (v[i]! - x0) * unitScale;
    v[i + 1] = bindHip.y + (v[i + 1]! - y0) * unitScale;
    v[i + 2] = bindHip.z + (v[i + 2]! - z0) * unitScale;
  }

  const t = hipTrack.times;
  const numFrames = t.length;
  const singleDuration = t[numFrames - 1]!;

  console.log(
    `[figure] locomotion clip normalised: maxDelta=${maxDelta.toFixed(2)} ${unitScale === 0.01 ? '(centimetres → metres)' : '(metres, no rescale)'} singleDuration=${singleDuration.toFixed(4)}s — rebased to bind hip (${bindHip.x.toFixed(2)}, ${bindHip.y.toFixed(2)}, ${bindHip.z.toFixed(2)}) strides=${strides}`,
  );

  if (strides <= 1) return;

  // Per-cycle displacement: how far does the hip travel in one cycle?
  // Only the position track needs this offset; all other tracks (rotations,
  // scales) are cyclic and repeat without an offset.
  const numHipFrames = t.length;
  const lastHipIdx = (numHipFrames - 1) * 3;
  const cycleDelX = v[lastHipIdx]!     - v[0]!;
  const cycleDelY = v[lastHipIdx + 1]! - v[1]!;
  const cycleDelZ = v[lastHipIdx + 2]! - v[2]!;

  // Stitch EVERY track in the clip so bone rotations loop for the full
  // walk duration instead of freezing at the last frame of cycle 1.
  // Position tracks (hip) accumulate the per-cycle displacement;
  // all other tracks (quaternion rotations, scales) just repeat.
  for (const track of clip.tracks) {
    const tV = track.values as Float32Array;
    const tT = track.times as Float32Array;
    const nFrames = tT.length;
    if (nFrames < 2) continue;

    const valPerFrame = tV.length / nFrames; // 3 = position/scale, 4 = quat
    const cycleDur = tT[nFrames - 1]!;
    const isHipPos = HIP_POSITION_TRACK_RE.test(track.name);

    const stitchedV = new Float32Array(strides * nFrames * valPerFrame);
    const stitchedT = new Float32Array(strides * nFrames);

    for (let s = 0; s < strides; s++) {
      for (let f = 0; f < nFrames; f++) {
        const srcBase = f * valPerFrame;
        const dstBase = (s * nFrames + f) * valPerFrame;
        for (let c = 0; c < valPerFrame; c++) {
          stitchedV[dstBase + c] = tV[srcBase + c]!;
        }
        if (isHipPos) {
          stitchedV[dstBase]     += s * cycleDelX;
          stitchedV[dstBase + 1] += s * cycleDelY;
          stitchedV[dstBase + 2] += s * cycleDelZ;
        }
        stitchedT[s * nFrames + f] = tT[f]! + s * cycleDur;
      }
    }

    (track as unknown as { values: Float32Array; times: Float32Array }).values = stitchedV;
    (track as unknown as { values: Float32Array; times: Float32Array }).times  = stitchedT;
  }

  clip.duration = strides * singleDuration;
}

/**
 * Some exporters (e.g. DeepMotion SayMotion) output the standard Mixamo
 * skeleton but without the `mixamorig:` namespace prefix. Three.js binds
 * animation tracks by exact bone name, so `Hips.quaternion` never matches
 * Josh's bone `mixamorigHips` and the character freezes in T-pose.
 *
 * If none of the tracks already carry the `mixamorig` prefix we assume the
 * clip is "bare Mixamo" and prepend it to every bone track name.
 */
function normalizeMixamoTracks(clip: AnimationClip): void {
  const hasMixamoPrefix = clip.tracks.some((t) => t.name.startsWith('mixamorig'));
  if (hasMixamoPrefix) return; // already correct

  for (const track of clip.tracks) {
    // Track names are like "Hips.quaternion" or "LeftArm.position".
    // Prepend the prefix to the bone portion only.
    const dot = track.name.indexOf('.');
    if (dot === -1) continue;
    const bone = track.name.slice(0, dot);
    const prop = track.name.slice(dot); // includes the dot
    track.name = `mixamorig${bone}${prop}`;
  }
}

/**
 * Validate that a loaded FBX has a Mixamo auto-rig skeleton by checking
 * for at least one bone whose name contains the `mixamorig` prefix (case-
 * insensitive). Returns the detected prefix style so the caller can log it.
 *
 * Two common export styles:
 *   - `mixamorig:Hips`  — FBX for Unity (correct; colon preserved).
 *   - `mixamorigHips`   — Some legacy exports (concatenated; also works
 *                          because Three.js strips the colon when building
 *                          property paths anyway).
 *
 * Any other naming (e.g. plain `Hips`, `Bip001 Pelvis`, `root`) means the
 * character was NOT run through Mixamo's auto-rigger and animations will
 * not bind.
 */
/**
 * Some character exporters (e.g. Avaturn GLB) use the standard Mixamo
 * skeleton but omit the `mixamorig:` namespace prefix on bone names.
 * Bones are named `Hips`, `Spine`, `LeftArm`, etc. rather than
 * `mixamorigHips`, `mixamorigSpine`, `mixamorigLeftArm`.
 *
 * Mixamo animation tracks target the prefixed names, so they fail to bind
 * silently and the character freezes in T-pose.
 *
 * If the root has no `mixamorig`-prefixed bones but does contain a bone
 * called `Hips` (the canonical Mixamo root joint), we rename every bone to
 * prepend `mixamorig`. After renaming, `checkMixamoRig` will pass and all
 * animation tracks will bind correctly.
 *
 * This is the character-side mirror of `normalizeMixamoTracks` which handles
 * the same problem in the animation direction (DeepMotion clips).
 */
function normalizeMixamoBones(root: Object3D): void {
  let hasMixamoPrefix = false;
  let hasHips = false;
  root.traverse((obj) => {
    if ((obj as { isBone?: boolean }).isBone || obj.type === 'Bone') {
      if (/^mixamorig/i.test(obj.name)) hasMixamoPrefix = true;
      if (obj.name === 'Hips') hasHips = true;
    }
  });

  if (hasMixamoPrefix || !hasHips) return; // already correct, or unrecognised rig

  root.traverse((obj) => {
    if ((obj as { isBone?: boolean }).isBone || obj.type === 'Bone') {
      obj.name = `mixamorig${obj.name}`;
    }
  });
}

function checkMixamoRig(root: Object3D): { valid: boolean; boneCount: number } {
  let boneCount = 0;
  let mixamoBones = 0;
  root.traverse((obj) => {
    if ((obj as { isBone?: boolean }).isBone) {
      boneCount++;
      if (/^mixamorig/i.test(obj.name)) mixamoBones++;
    }
  });
  return { valid: mixamoBones >= 10, boneCount };
}

const fbxLoader = new FBXLoader();
const gltfLoader = new GLTFLoader();

/** Load a character model from either an FBX or GLB/GLTF file. */
async function loadCharacterModel(url: string): Promise<Group> {
  const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
  if (ext === 'glb' || ext === 'gltf') {
    const gltf = await gltfLoader.loadAsync(url);
    return gltf.scene as unknown as Group;
  }
  return fbxLoader.loadAsync(url) as unknown as Promise<Group>;
}

export async function loadFigure(
  url: string,
  opts: LoadFigureOpts = {},
): Promise<Figure> {
  const root = await loadCharacterModel(url);
  root.name = 'figure';

  // Captured here so inner closures (playAdditiveClipFromUrl, fetchRawClip)
  // can reach it without `opts` getting shadowed by their own option params.
  const rigProfile: RigProfile | undefined = opts.rigProfile;

  // GLB/GLTF is already in metres; FBX from Mixamo is in centimetres.
  const isGlb = /\.(glb|gltf)(\?|$)/i.test(url);
  const scale = opts.scale ?? (isGlb ? 1.0 : 0.01);
  root.scale.setScalar(scale);

  // CC GLB exports (Riya, Anne) stack body skin + clothing on the same
  // skeleton — without explicit depth offsets the layers z-fight as the
  // camera moves. See `cc-glb-materials.ts` for the patch details.
  const isCcRig = rigProfile === 'cc-base' || rigProfile === 'cc-base-noprefix';
  if (isGlb && isCcRig) {
    // Always strip Blender / Faceit author-time helpers (facial_landmarks,
    // backdrop planes, FaceitRig, cs_fist, …) — these would otherwise
    // render as floating geometry / white spheres over the character.
    const stripped = stripBlenderStudioExtras(root);
    if (stripped > 0) {
      console.log(`[loadFigure] stripped ${stripped} Blender/Faceit studio extra(s) from GLB`);
    }
    if (BODY_TEXTURED) {
      fixCcGlbLayeringMaterials(root);
      fixCcTransparentMaterials(root);
      if (opts.hideSkinSlots && opts.hideSkinSlots.length > 0) {
        hideBodySkinSlots(root, opts.hideSkinSlots);
      }
      console.log('[loadFigure] cc-base GLB: clothing/skin depth offset + hair alpha fix');
    }
  }

  // Normalise bare Mixamo bone names (e.g. Avaturn GLB exports `Hips` instead
  // of `mixamorigHips`). Must run before checkMixamoRig so the prefix check passes.
  // Skipped for non-Mixamo rig profiles — they have their own bone naming
  // (CC_Base_Hip, etc.) and forcing a `mixamorig` prefix would break it.
  const isNonMixamoProfile =
    rigProfile === 'cc-base' ||
    rigProfile === 'cc-base-noprefix' ||
    rigProfile === 'rigify-stretch' ||
    rigProfile === 'metahuman' ||
    rigProfile === 'epic-skeleton';
  if (!isNonMixamoProfile) {
    normalizeMixamoBones(root);
  }

  // Validate rig before doing anything else. An incompatible rig causes
  // every animation track to fail silently, leaving the character in T-pose
  // with a wall of "No target node found" warnings in the console. Better to
  // surface a clear error immediately so the user knows the problem is the
  // source file, not the code.
  //
  // For non-Mixamo profiles we skip this check entirely — the rig is
  // expected to NOT have `mixamorig` bones, that's the whole point of the
  // rename pass. Track-level bind warnings from the mixer will still
  // surface any leftover mismatches at clip-load time.
  if (!isNonMixamoProfile) {
    const rigCheck = checkMixamoRig(root);
    if (!rigCheck.valid) {
      throw new Error(
        `[loadFigure] "${url}" does not appear to be a Mixamo auto-rigged character ` +
          `(found ${rigCheck.boneCount} bones, none with the "mixamorig" prefix). ` +
          `Fix: open Mixamo → upload this character → let it auto-rig → ` +
          `download as "FBX for Unity" with T-pose selected.`,
      );
    }
    console.log(`[loadFigure] loaded "${url}" — ${rigCheck.boneCount} Mixamo bones OK`);
  } else {
    let totalBones = 0;
    root.traverse((o) => { if ((o as { isBone?: boolean }).isBone) totalBones++; });
    console.log(`[loadFigure] loaded "${url}" — profile=${rigProfile}, ${totalBones} bones`);
  }

  // ── cc-base retargeter setup ──────────────────────────────────────────
  //
  // For non-Mixamo profiles we need to retarget every clip the figure
  // plays — Mixamo-named tracks won't bind to cc-base bones, and even if
  // they did, the bind-pose differences between the source (Lewis,
  // standard Mixamo T-pose) and target (Riya, cc-base T-pose with
  // different per-bone local axes) would deform the character. The
  // retargeter handles both: name remap + bind-pose-aware quaternion
  // retargeting + custom world-space hip translation.
  //
  // Built once here, cached in the closure, used by `fetchRawClip` and
  // `playAdditiveClipFromUrl` below. Profiles with no target patterns
  // registered in `cc-retarget.ts` (currently `metahuman`) get a null
  // context — clips pass through unchanged, which is fine because we're
  // not shipping animation support for those profiles in the main app yet.
  //
  // Setup runs before any clip is loaded, so the bind pose is intact when
  // we snapshot hip world positions.
  let ccRetargetCtx: CcRetargetContext | null = null;
  if (rigProfile && rigProfile !== 'mixamo' && rigProfile !== 'avaturn') {
    try {
      ccRetargetCtx = await setupCcRetargeter(root, rigProfile);
      if (ccRetargetCtx) {
        console.log(
          `[loadFigure] cc-retargeter ready · src "${ccRetargetCtx.srcHipName}" → trg "${ccRetargetCtx.trgHipName}"`,
        );
      }
    } catch (err) {
      console.warn('[loadFigure] cc-retargeter setup failed — clips will play unretargeted:', err);
      ccRetargetCtx = null;
    }
  }

  const hides = opts.hideNamePatterns ?? [];
  if (hides.length) {
    root.traverse((obj) => {
      if (hides.some((re) => re.test(obj.name))) {
        obj.visible = false;
      }
    });
  }

  // ── Wireframe + fill rendering ────────────────────────────────────────
  //
  // Both materials are shared across every submesh — Mixamo characters
  // arrive split into a handful of pieces (skin, clothing, hair, etc.)
  // and we want them to read as a single mannequin surface. Sharing also
  // means future runtime tweaks (opacity envelopes, colour breathing,
  // etc.) can target one material instance and affect the whole body.
  //
  // Critical: the fill writes to depth (`depthWrite: true`) even though
  // it's transparent. Without this, every wire on the *back* of the body
  // shows through the front, producing a tangled cage instead of a clean
  // silhouette. With it, the front surface of the fill occludes the back
  // surface of the same mesh, and the wireframe (rendered second via
  // `renderOrder + 1`) only paints the front. Fill uses `polygonOffset`
  // to push its depth values slightly away from the camera so the
  // wireframe lines win the depth test on the front faces and don't
  // z-fight with the fill underneath.
  const fillMaterial = !BODY_TEXTURED
    ? new MeshBasicMaterial({
        color: new Color(FILL_COLOR),
        transparent: true,
        opacity: FILL_OPACITY,
        depthWrite: true,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      })
    : null;
  const wireMaterial = !BODY_TEXTURED
    ? new MeshBasicMaterial({
        color: new Color(WIRE_COLOR),
        wireframe: true,
        transparent: true,
        opacity: WIRE_OPACITY,
        depthWrite: false,
      })
    : null;

  // First pass: collect SkinnedMeshes. We mutate the hierarchy below
  // (adding wireframe siblings) so we can't combine collection and
  // mutation into a single `traverse` — the new siblings would be
  // re-visited and double-cloned.
  const skinnedMeshes: SkinnedMesh[] = [];
  const plainMeshes: Mesh[] = [];
  root.traverse((obj) => {
    if (!hierarchicallyVisible(obj)) return;
    if ((obj as SkinnedMesh).isSkinnedMesh) {
      skinnedMeshes.push(obj as SkinnedMesh);
    } else if ((obj as Mesh).isMesh) {
      plainMeshes.push(obj as Mesh);
    }
  });

  // Collect morph-target meshes and log available blendshapes for diagnostics.
  // Search BOTH SkinnedMeshes and plain Meshes — some GLB exporters (e.g.
  // Avaturn) place facial blendshapes on a non-skinned face mesh.
  const allMeshes: (SkinnedMesh | Mesh)[] = [...skinnedMeshes, ...plainMeshes];
  const morphMeshes = allMeshes.filter(
    (m) => m.morphTargetDictionary && Object.keys(m.morphTargetDictionary).length > 0,
  ) as (SkinnedMesh | Mesh)[];
  const morphTargetNames = [
    ...new Set(morphMeshes.flatMap((m) => Object.keys(m.morphTargetDictionary!))),
  ];
  if (morphTargetNames.length > 0) {
    console.log(`[loadFigure] morph targets (${morphTargetNames.length}) on ${morphMeshes.length} mesh(es):`, morphTargetNames.join(', '));
  } else {
    // Diagnostic: list all mesh names so we can see what's in the model
    const meshNames = allMeshes.map(m => m.name || '(unnamed)');
    console.log(`[loadFigure] no morph targets found. Meshes in model (${allMeshes.length}):`, meshNames.join(', '));
  }

  // ── Morph-name alias index ──────────────────────────────────────────────
  //
  // The expression / viseme / blink / brow / saccade layers all WRITE keys
  // in canonical ARKit camelCase (`mouthSmileLeft`, `eyeBlinkLeft`, …). The
  // mesh's actual morph names may be PascalCase (Asuna), underscored caps
  // (Riya / Anne CC), or already-canonical (Avaturn / RPM). The alias index
  // resolves both directions so the per-frame mixer can read a layer keyed
  // canonical and apply it to a mesh keyed in any convention.
  // Pass `rigProfile` so the alias builder can apply the CC explicit table
  // (Riya, Anne) — without it, CC names like `Mouth_Smile_L` won't resolve
  // to canonical `mouthSmileLeft` and emotions / blinks / saccades stay dark.
  const morphAliases: MorphAliasIndex = buildMorphAliasIndex(morphTargetNames, {
    rigProfile: rigProfile === 'cc-base' || rigProfile === 'cc-base-noprefix'
      ? rigProfile
      : undefined,
  });
  if (morphTargetNames.length > 0) {
    console.log(
      `[loadFigure] ARKit face shapes resolved: ${morphAliases.canonicalToActual.size}/${morphTargetNames.length} morph targets`,
    );
  }

  /** Resolve a mesh morph name back to its canonical ARKit key, or itself. */
  function canonicalForMorph(actualName: string): string {
    return morphAliases.actualToCanonical.get(actualName) ?? actualName;
  }

  /**
   * Look up a layer weight for a mesh morph by either its actual rig name
   * (Avaturn writes camelCase keys directly) or its canonical ARKit alias
   * (CC / Asuna writes go through canonical). Falls through to 0.
   */
  function layerWeight(
    layer: Record<string, number>,
    actualName: string,
    canonical: string,
  ): number {
    return layer[actualName] ?? layer[canonical] ?? 0;
  }

  // Reverse index: mesh → (influenceIndex → ARKit name). Built once at load
  // time so the per-frame lerp loop doesn't need to iterate dictionary keys.
  const morphIndexToName = new Map<SkinnedMesh | Mesh, Map<number, string>>();
  for (const mesh of morphMeshes) {
    if (!mesh.morphTargetDictionary) continue;
    const rev = new Map<number, string>();
    for (const [name, idx] of Object.entries(mesh.morphTargetDictionary)) {
      rev.set(idx, name);
    }
    morphIndexToName.set(mesh, rev);
  }

  // ── Layered expression system ───────────────────────────────────────────
  //
  // The face is driven by THREE independent layers that are max-blended in
  // the update loop, so each layer can override or co-exist without fighting:
  //
  //   1. ambient  — always-on baseline (subtle thinking expression). Set
  //                 once at startup via setAmbient(). Survives between beats.
  //   2. beat     — emotion preset for the current beat. Replaced each
  //                 time setExpression() is called. Empty preset means
  //                 "no beat-specific expression — let ambient show".
  //   3. procedural — blink (full closure ~130 ms), brow micro-twitches
  //                   (small ±0.15 brow shifts every 1.5–4 s). These are
  //                   driven by their own timers and overlay the others.
  //
  // The per-shape final value is `max(ambient, beat, procedural)` so the
  // strongest expression wins and dropping a layer reveals the one beneath.

  const EXPRESSION_SMOOTH_SEC = 0.6; // beat-emotion lerp time (faster than before for visibility)
  const AMBIENT_LERP_SEC = 1.5;      // ambient changes are slow & calm
  const VISEME_LERP_SEC = 0.05;      // visemes change with audio — keep responsive
  let beatTarget: Record<string, number> = {};
  let ambientTarget: Record<string, number> = {};
  /**
   * Fourth layer — lip-sync visemes from the narration system. Replaced on
   * every narration tick. CC rigs receive the post-`expandCcBaseVisemeWeights`
   * dict (mixed canonical + CC names like `V_Open`); other rigs receive
   * canonical ARKit weights as-is.
   */
  let visemeTarget: Record<string, number> = {};

  // ── CC jaw bone rotation ─────────────────────────────────────────────────
  //
  // On Riya / CC GLB exports, the visible mouth opening comes from rotating
  // `CC_Base_JawRoot` around its local +Z axis — NOT from the `Jaw_Open`
  // morph (which moves vertices ~0.2 mm and is invisible without the bone
  // hinge). The `Female_Angled` face overlay is skinned to this bone, so
  // when it rotates the visible face surface follows.
  //
  // Constants (axis [0,0,1], 0.4 rad max) live in `cc-face-rig.ts`. They
  // were measured per-character in the previous-chat workflow and shipped
  // as the calibrated jaw hinge for Riya. We reuse them as the cc-base
  // default — Anne (`cc-base-noprefix`) has no `Jaw_Open` morph and her
  // bone naming may differ, so this code does not run for her.
  let ccJawBone: Object3D | null = null;
  let ccJawBindQuat: Quaternion | null = null;
  const ccJawAxis = new Vector3(...CC_JAW_OPEN_AXIS);
  const ccJawDelta = new Quaternion();
  let ccJawOpenCurrent = 0;
  if (rigProfile === 'cc-base') {
    root.traverse((obj) => {
      if (ccJawBone) return;
      if (CC_JAW_BONE_NAME_PATTERN.test(obj.name)) ccJawBone = obj;
    });
    if (ccJawBone) {
      ccJawBindQuat = (ccJawBone as Object3D).quaternion.clone();
      console.log(
        `[loadFigure] cc-base jaw bone: ${(ccJawBone as Object3D).name}`
        + ` (axis [${CC_JAW_OPEN_AXIS.join(', ')}], ${CC_JAW_OPEN_RADIANS} rad max)`,
      );
    } else {
      console.warn('[loadFigure] cc-base: CC_Base_JawRoot not found — jaw bone hinge disabled');
    }
  }

  // ── Emotion-driven eye / brow profile ────────────────────────────────────
  //
  // Blink rate, saccade pattern and brow micro-twitches all read very
  // differently across emotional states. An anxious person blinks twice as
  // often as a calm one and scans laterally; a sad person looks down and
  // holds longer blinks; a focused / surprised person barely blinks at all.
  // We capture this with a small profile per emotion archetype, plus an
  // energy multiplier (high arousal = faster everything).
  //
  // The BeatPlayer calls figure.setEmotionState(emotion, intensity, energy)
  // on every beat. We swap the active profile and the energy multiplier;
  // the existing schedule loops pick up the new rates on their next tick
  // (so changes feel like a natural shift, not a hard reset).

  type EyeProfile = {
    /** [min, max] ms between blinks. Lower = more anxious / aroused. */
    blinkIntervalMs: [number, number];
    /** [min, max] ms the lid stays closed. Longer = sad / tired. */
    blinkHoldMs: [number, number];
    /** [min, max] ms between saccades. Lower = vigilant / scanning. */
    saccadeIntervalMs: [number, number];
    /** [min, max] ms the gaze fixates after each saccade. */
    saccadeFixationMs: [number, number];
    /** [min, max] horizontal saccade amplitude (blendshape weight). */
    saccadeAmpH: [number, number];
    /** [min, max] vertical saccade amplitude (blendshape weight). */
    saccadeAmpV: [number, number];
    /** 0..1 — probability each saccade looks down rather than up. */
    saccadeDownBias: number;
    /** [min, max] ms between brow micro-twitches. */
    browIntervalMs: [number, number];
    /** [min, max] brow twitch amplitude. */
    browAmp: [number, number];
    /**
     * Probability weights for brow twitch flavour. Need not sum to 1 —
     * we normalise at pick-time. Set a flavour to 0 to disable it.
     */
    browFlavour: {
      inner_lift: number;
      furrow: number;
      outer_lift: number;
      asymmetric: number;
    };
  };

  const NEUTRAL_PROFILE: EyeProfile = {
    blinkIntervalMs: [2500, 5500],
    blinkHoldMs: [180, 240],
    saccadeIntervalMs: [1500, 3500],
    saccadeFixationMs: [350, 800],
    saccadeAmpH: [0.20, 0.45],
    saccadeAmpV: [0.10, 0.30],
    saccadeDownBias: 0.40,
    browIntervalMs: [1800, 4000],
    browAmp: [0.12, 0.30],
    browFlavour: { inner_lift: 0.40, furrow: 0.25, outer_lift: 0.15, asymmetric: 0.20 },
  };

  // Per-emotion profiles. Anything not listed falls back to NEUTRAL_PROFILE
  // via getEyeProfileFor() below. Values are tuned, not derived — but each
  // diverges from neutral along an obvious axis (rate, hold, gaze bias,
  // brow flavour) so the difference is legible at viewing distance.
  const EYE_PROFILES: Record<string, EyeProfile> = {
    neutral: NEUTRAL_PROFILE,
    calm: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [3500, 7000],   // slower
      saccadeIntervalMs: [2500, 5000], // less scanning
      saccadeAmpH: [0.15, 0.35],       // smaller eye movements
      browFlavour: { inner_lift: 0.45, furrow: 0.10, outer_lift: 0.25, asymmetric: 0.20 },
    },
    bored: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [3000, 6500],
      blinkHoldMs: [220, 320],         // heavier lids
      saccadeIntervalMs: [3000, 6000], // disengaged
      saccadeDownBias: 0.55,
      browIntervalMs: [3000, 6000],    // less expression
      browFlavour: { inner_lift: 0.20, furrow: 0.20, outer_lift: 0.10, asymmetric: 0.50 },
    },
    tired: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [2200, 4500],
      blinkHoldMs: [280, 400],         // very heavy lids
      saccadeIntervalMs: [3500, 7000],
      saccadeAmpH: [0.10, 0.25],
      saccadeAmpV: [0.05, 0.18],
      saccadeDownBias: 0.65,
      browIntervalMs: [3500, 7000],
      browAmp: [0.08, 0.18],
      browFlavour: { inner_lift: 0.55, furrow: 0.15, outer_lift: 0.05, asymmetric: 0.25 },
    },
    sadness: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [3000, 6000],
      blinkHoldMs: [240, 360],
      saccadeIntervalMs: [2500, 5000],
      saccadeAmpH: [0.15, 0.30],
      saccadeAmpV: [0.10, 0.25],
      saccadeDownBias: 0.75,           // looks down a lot
      browIntervalMs: [1800, 3500],
      browFlavour: { inner_lift: 0.65, furrow: 0.15, outer_lift: 0.05, asymmetric: 0.15 },
    },
    defeated: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [2800, 5500],
      blinkHoldMs: [260, 380],
      saccadeIntervalMs: [3000, 6000],
      saccadeDownBias: 0.80,           // strongly downward
      browFlavour: { inner_lift: 0.60, furrow: 0.20, outer_lift: 0.05, asymmetric: 0.15 },
    },
    joy: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [2200, 5000],
      saccadeIntervalMs: [1800, 4000],
      saccadeAmpH: [0.20, 0.40],
      saccadeDownBias: 0.30,           // looks up/around more
      browFlavour: { inner_lift: 0.20, furrow: 0.05, outer_lift: 0.50, asymmetric: 0.25 },
    },
    excitement: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [1800, 4000],   // faster
      saccadeIntervalMs: [1200, 2800], // scanning excitedly
      saccadeAmpH: [0.25, 0.50],
      saccadeDownBias: 0.20,           // looking up
      browIntervalMs: [1200, 3000],
      browAmp: [0.18, 0.35],
      browFlavour: { inner_lift: 0.15, furrow: 0.05, outer_lift: 0.55, asymmetric: 0.25 },
    },
    triumphant: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [2200, 4800],
      saccadeIntervalMs: [1800, 4000],
      saccadeDownBias: 0.25,
      browFlavour: { inner_lift: 0.10, furrow: 0.10, outer_lift: 0.55, asymmetric: 0.25 },
    },
    fear: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [3500, 7000],   // freeze response — rare blinks
      blinkHoldMs: [120, 180],         // and quick when they happen
      saccadeIntervalMs: [600, 1800],  // rapid scanning
      saccadeFixationMs: [180, 450],   // short fixations
      saccadeAmpH: [0.30, 0.55],
      saccadeAmpV: [0.15, 0.30],
      saccadeDownBias: 0.30,
      browIntervalMs: [1000, 2500],
      browAmp: [0.20, 0.40],
      browFlavour: { inner_lift: 0.30, furrow: 0.05, outer_lift: 0.55, asymmetric: 0.10 },
    },
    tense: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [1500, 3500],   // anxious blink rate
      blinkHoldMs: [140, 200],
      saccadeIntervalMs: [800, 2200],  // rapid scanning
      saccadeFixationMs: [220, 500],
      saccadeAmpH: [0.25, 0.50],
      saccadeDownBias: 0.40,
      browIntervalMs: [1200, 3000],
      browAmp: [0.15, 0.30],
      browFlavour: { inner_lift: 0.20, furrow: 0.50, outer_lift: 0.10, asymmetric: 0.20 },
    },
    anger: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [2000, 4000],
      blinkHoldMs: [120, 180],         // sharp closure
      saccadeIntervalMs: [1200, 3000],
      saccadeAmpH: [0.20, 0.40],
      saccadeAmpV: [0.08, 0.20],       // narrowed gaze, less vertical
      saccadeDownBias: 0.45,
      browIntervalMs: [1500, 3500],
      browAmp: [0.20, 0.40],
      browFlavour: { inner_lift: 0.05, furrow: 0.75, outer_lift: 0.05, asymmetric: 0.15 },
    },
    disgust: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [2500, 5000],
      saccadeIntervalMs: [2000, 4500],
      saccadeAmpH: [0.15, 0.35],
      saccadeAmpV: [0.05, 0.18],       // narrowed
      browIntervalMs: [2000, 4000],
      browFlavour: { inner_lift: 0.10, furrow: 0.65, outer_lift: 0.10, asymmetric: 0.15 },
    },
    surprise: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [4000, 8000],   // wide-eyed: rare blinks
      blinkHoldMs: [100, 160],         // and very quick
      saccadeIntervalMs: [2500, 5500], // gaze locks
      saccadeFixationMs: [600, 1400],  // long fixations
      saccadeAmpH: [0.10, 0.30],
      browIntervalMs: [2500, 5000],
      browAmp: [0.20, 0.40],
      browFlavour: { inner_lift: 0.15, furrow: 0.05, outer_lift: 0.70, asymmetric: 0.10 },
    },
    disbelief: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [3000, 6000],
      saccadeIntervalMs: [1800, 4000],
      saccadeAmpH: [0.20, 0.45],
      browFlavour: { inner_lift: 0.15, furrow: 0.15, outer_lift: 0.40, asymmetric: 0.30 },
    },
    confused: {
      ...NEUTRAL_PROFILE,
      blinkIntervalMs: [2200, 4800],
      saccadeIntervalMs: [1200, 2800], // scanning for cues
      saccadeAmpH: [0.20, 0.45],
      saccadeDownBias: 0.35,
      browIntervalMs: [1500, 3000],
      browFlavour: { inner_lift: 0.30, furrow: 0.20, outer_lift: 0.10, asymmetric: 0.40 }, // asymmetric heavy
    },
  };

  function getEyeProfileFor(emotion: string): EyeProfile {
    return EYE_PROFILES[emotion] ?? NEUTRAL_PROFILE;
  }

  // Active profile + arousal multiplier. arousalScale is a single factor
  // applied to all interval ranges: <1 = faster, >1 = slower. Driven by
  // beat.energy (and to a lesser extent intensity) in setEmotionState.
  let currentEyeProfile: EyeProfile = NEUTRAL_PROFILE;
  let arousalScale = 1;

  function rangePick(range: [number, number], scale = 1): number {
    return (range[0] + Math.random() * (range[1] - range[0])) * scale;
  }

  // ── Autonomous eye blink ─────────────────────────────────────────────────
  //
  // Detects whichever blink shape names this model uses (standard ARKit has
  // eyeBlinkLeft + eyeBlinkRight; some exporters use a single eyesClosed).
  // Runs a random-interval blink timer independent of the expression system.
  // In the per-frame loop, blink is overlaid on top of the emotion target so
  // the two systems never fight each other.

  const BLINK_LERP_SEC = 0.10;  // ~100 ms close → 220 ms hold → ~100 ms open. More visible.
  let blinkValue = 0;
  let blinkTarget = 0;

  const blinkShapeNames: string[] = morphAliases.resolveActual(
    // Use all available eye-close shapes together — on Avaturn characters
    // eyeBlinkLeft/Right are partial (~30%) and eyesClosed completes the
    // closure. The alias index resolves PascalCase / CC underscore variants
    // (e.g. Asuna's `EyeBlinkLeft`, Riya's `Eye_Blink_L`) to actual rig names.
    'eyeBlinkLeft', 'eyeBlinkRight', 'eyesClosed', 'eyeBlink',
  );

  let blinkTimerId: ReturnType<typeof setTimeout> | null = null;

  function scheduleBlink(): void {
    if (blinkShapeNames.length === 0) return;
    // Interval and hold are pulled from the active emotion profile and
    // scaled by arousal (high energy = faster blink rate).
    const intervalMs = rangePick(currentEyeProfile.blinkIntervalMs, arousalScale);
    blinkTimerId = setTimeout(() => {
      blinkTarget = 1; // close fully
      const holdMs = rangePick(currentEyeProfile.blinkHoldMs);
      blinkTimerId = setTimeout(() => {
        blinkTarget = 0; // open
        scheduleBlink();
      }, holdMs);
    }, intervalMs);
  }

  if (blinkShapeNames.length > 0) {
    console.log(`[loadFigure] blink shapes: ${blinkShapeNames.join(', ')}`);
    scheduleBlink();
  }

  // ── Brow micro-twitches ──────────────────────────────────────────────────
  //
  // Subliminal "thinking" tells. Every 1.5–4 s we briefly lift or furrow
  // the brows by ±0.20 over ~250 ms, then release. Reads as the character
  // having a thought without committing to a full expression.
  const BROW_LERP_SEC = 0.15;
  let browTwitchValue: Record<string, number> = {};
  let browTwitchTarget: Record<string, number> = {};
  const BROW_SHAPE_NAMES = ['browInnerUp', 'browOuterUpLeft', 'browOuterUpRight', 'browDownLeft', 'browDownRight'];
  // Canonical-name lookup so PascalCase / CC underscore brow shapes register too.
  const hasBrows = BROW_SHAPE_NAMES.some((s) => morphAliases.hasCanonical(s));
  let browTwitchTimerId: ReturnType<typeof setTimeout> | null = null;

  function scheduleBrowTwitch(): void {
    if (!hasBrows) return;
    const intervalMs = rangePick(currentEyeProfile.browIntervalMs, arousalScale);
    browTwitchTimerId = setTimeout(() => {
      const amp = rangePick(currentEyeProfile.browAmp);
      // Pick a flavour weighted by the active profile. Normalise weights so
      // a profile that zeroes a flavour (e.g. surprise: furrow=0.05) won't
      // pick that flavour by accident.
      const w = currentEyeProfile.browFlavour;
      const total = w.inner_lift + w.furrow + w.outer_lift + w.asymmetric;
      const r = Math.random() * (total > 0 ? total : 1);
      let acc = 0;
      let flavour: 'inner_lift' | 'furrow' | 'outer_lift' | 'asymmetric' = 'inner_lift';
      acc += w.inner_lift; if (r < acc) flavour = 'inner_lift';
      else { acc += w.furrow; if (r < acc) flavour = 'furrow';
      else { acc += w.outer_lift; if (r < acc) flavour = 'outer_lift';
      else flavour = 'asymmetric'; } }

      if (flavour === 'inner_lift') {
        browTwitchTarget = { browInnerUp: amp };
      } else if (flavour === 'furrow') {
        browTwitchTarget = { browDownLeft: amp * 0.7, browDownRight: amp * 0.7, browInnerUp: amp * 0.3 };
      } else if (flavour === 'outer_lift') {
        browTwitchTarget = { browOuterUpLeft: amp, browOuterUpRight: amp };
      } else {
        const leftSide = Math.random() < 0.5;
        browTwitchTarget = leftSide
          ? { browOuterUpLeft: amp }
          : { browOuterUpRight: amp };
      }
      // Hold the twitch briefly, then release
      browTwitchTimerId = setTimeout(() => {
        browTwitchTarget = {};
        scheduleBrowTwitch();
      }, 250 + Math.random() * 350); // 250–600 ms hold
    }, intervalMs);
  }

  if (hasBrows) {
    console.log(`[loadFigure] brow shapes available — micro-twitches enabled`);
    scheduleBrowTwitch();
  }

  // ── Procedural idle noise (Perlin-like sum-of-sines) ─────────────────────
  //
  // The character feels "alive" because tiny non-repeating motions are layered
  // on top of whatever the mixer is doing. Two layers:
  //   1. Head sway — sub-degree rotation on the head bone (yaw/pitch/roll)
  //   2. Hips drift — sub-cm horizontal translation on the hips bone
  //      (asymmetric so the centre of mass biases naturally)
  //
  // Both use sum-of-sines with irrational frequency ratios so the pattern
  // never visibly repeats (Perlin-equivalent without the lookup tables).
  //
  // ── Why save/restore is required ─────────────────────────────────────────
  //
  // Naively `bone.rotation.y += yaw` accumulates each frame: when the active
  // clip animates the head, the mixer overwrites our previous frame's
  // contribution and `+=` adds correctly. But when the active clip does NOT
  // animate the head bone (common with Uthana clips), our offset compounds
  // every frame and the head spins to extreme angles within seconds.
  //
  // Solution: BEFORE `mixer.update()`, restore the bone to the value we
  // saved last frame *prior to* applying our offset. After the mixer runs,
  // save the new clean baseline (mixer-written or unchanged), then apply
  // our offset. This works in both cases without needing to inspect tracks.

  // Head/hips lookup. The Mixamo names (`mixamorigHead`, `mixamorigHips`)
  // cover Lewis / Iron Dude / Office Girl / RPM / Avaturn (after
  // normalizeMixamoBones). cc-base characters (Riya) use `CC_Base_Head` /
  // `CC_Base_Hip`. cc-base-noprefix (Anne Hathaway-style Blender exports)
  // use bare `Head` / `Hip`. Rigify (Lion Man) uses `rootx` / `root.x`.
  // Epic Skeleton (Viking) uses `pelvis`. This regex MUST stay in sync
  // with `HIP_POSITION_TRACK_RE` in cc-retarget.ts — they're the
  // bone-discovery and track-discovery sides of the same lookup; if one
  // omits a rig, locomotion / camera-follow / bind-hip silently fall
  // back to the figure root and the hip stops translating in world space.
  let headBone: import('three').Object3D | null = null;
  let hipsBone: import('three').Object3D | null = null;
  const HEAD_RE = /^(?:mixamorigHead|CC_Base_Head|Head|head(?:\.x|x))$/i;
  const HIPS_RE = /^(?:mixamorigHips|CC_Base_Hip|Hips?|pelvis|root(?:\.x|x))$/i;
  root.traverse((obj) => {
    if (!headBone && HEAD_RE.test(obj.name)) headBone = obj;
    if (!hipsBone && HIPS_RE.test(obj.name)) hipsBone = obj;
  });
  console.log(`[loadFigure] headBone: ${headBone ? (headBone as import('three').Object3D).name : 'NOT FOUND'}`);
  console.log(`[loadFigure] hipsBone: ${hipsBone ? (hipsBone as import('three').Object3D).name : 'NOT FOUND'}`);

  // Bind-pose hip position — captured ONCE at load time, before any clip
  // plays. This is the figure-local position of mixamorigHips in its rest
  // pose, used as the reference point when normalising locomotion clips
  // (see prepareLocomotionClip). Without this, every locomotion clip would
  // pop the hip to its own frame-0 position, which on Mixamo FBX can be
  // hundreds of metres above the ground (cm-vs-m unit mismatch) or a few
  // metres laterally (clip recorded at a non-zero start position).
  const bindHipPosition: { x: number; y: number; z: number } = hipsBone
    ? {
        x: (hipsBone as import('three').Object3D).position.x,
        y: (hipsBone as import('three').Object3D).position.y,
        z: (hipsBone as import('three').Object3D).position.z,
      }
    : { x: 0, y: 0, z: 0 };
  console.log(
    `[loadFigure] hip bind position: (${bindHipPosition.x.toFixed(3)}, ${bindHipPosition.y.toFixed(3)}, ${bindHipPosition.z.toFixed(3)})`,
  );

  // Save/restore state — snapshots taken AFTER mixer.update() each frame,
  // representing the "clean" baseline we then add our offset on top of.
  // First-frame flag prevents the very first restore from clobbering bind pose.
  let headSaved: { x: number; y: number; z: number } | null = null;
  let hipsSaved: { x: number; y: number; z: number } | null = null;

  // ── Procedural body bone discovery ──────────────────────────────────────
  //
  // Walk the skeleton once and resolve every BoneSlot in BONE_PATTERNS to
  // its matching Object3D (or null if the rig doesn't have it). The body
  // gesture / body state / procedural hold systems all reference bones
  // by slot name, so this map is the single binding point.
  //
  // Slots that don't resolve are silently skipped at apply time — a
  // simplified rig (e.g. no Spine2) still works, it just can't be touched
  // by anything that targets that slot.
  const bonesBySlot: Partial<Record<BoneSlot, import('three').Object3D>> = {};
  // Bind-pose local quaternions, snapshotted at the moment we resolve
  // each bone — i.e. before any mixer or additive layer has had a chance
  // to mutate `bone.quaternion`. The SMPL→Mixamo retargeter needs these
  // to convert SMPL world-space rotations into Mixamo bone-local Euler
  // offsets correctly. See `src/motion/smpl-retarget.ts`.
  //
  // We store both the quaternion and the Euler XYZ form because the
  // retargeter computes its offsets in Euler space (matching the
  // additive-layer semantics) but the FK pass operates on quaternions.
  const bindRotationsBySlot: Partial<Record<BoneSlot, {
    qx: number; qy: number; qz: number; qw: number;
    ex: number; ey: number; ez: number;
  }>> = {};
  root.traverse((obj) => {
    for (const slot of ALL_BONE_SLOTS) {
      if (!bonesBySlot[slot] && BONE_PATTERNS[slot].test(obj.name)) {
        bonesBySlot[slot] = obj;
        const q = obj.quaternion;
        const e = obj.rotation;
        bindRotationsBySlot[slot] = {
          qx: q.x, qy: q.y, qz: q.z, qw: q.w,
          ex: e.x, ey: e.y, ez: e.z,
        };
      }
    }
  });
  const foundSlots = (Object.keys(bonesBySlot) as BoneSlot[]).filter((s) => bonesBySlot[s]);
  console.log(`[loadFigure] body slots resolved: ${foundSlots.length}/${ALL_BONE_SLOTS.length}`);
  if (foundSlots.length < ALL_BONE_SLOTS.length) {
    const missing = ALL_BONE_SLOTS.filter((s) => !bonesBySlot[s]);
    console.log(`[loadFigure] missing slots: ${missing.join(', ')}`);
  }

  // Per-bone post-mixer baselines, captured each frame after mixer.update.
  // Same save/restore pattern as headSaved/hipsSaved — restore BEFORE the
  // next mixer.update so previous-frame additive offsets don't accumulate
  // when the mixer doesn't write that bone (clip has no track for it).
  const boneSavedRotations: Partial<Record<BoneSlot, { x: number; y: number; z: number }>> = {};


  // Eye saccade state — gaze direction as blendshape weight targets.
  // We drive four shapes: eyeLookInLeft/Right (converge) and
  // eyeLookOutLeft/Right (diverge) for horizontal, plus Up/Down verticals.
  const EYE_SACCADE_SHAPES = [
    'eyeLookInLeft', 'eyeLookOutLeft', 'eyeLookInRight', 'eyeLookOutRight',
    'eyeLookUpLeft', 'eyeLookUpRight', 'eyeLookDownLeft', 'eyeLookDownRight',
  ];
  // Canonical-name lookup — handles PascalCase (Asuna `EyeLookInLeft`) and
  // CC (`Eye_L_Look_In` / `Eye_R_Look_In`) without per-rig casing branches.
  const hasEyeSaccades = EYE_SACCADE_SHAPES.some((s) => morphAliases.hasCanonical(s));

  let eyeTarget: Record<string, number> = {};
  let eyeCurrent: Record<string, number> = Object.fromEntries(EYE_SACCADE_SHAPES.map((s) => [s, 0]));
  const EYE_SACCADE_SMOOTH = 0.08; // 80 ms — real saccades are fast (~50–200 ms)
  let eyeSaccadeTimerId: ReturnType<typeof setTimeout> | null = null;

  // ── Director-scripted gaze override ──────────────────────────────────────
  //
  // When a beat sets `gaze: 'down' | 'up_left' | …`, the BeatPlayer calls
  // setGazeOverride() to lock the eyes to that target for the duration of
  // the beat. Procedural saccades are suppressed while an override is
  // active; on null they resume.
  //
  // The override stores the pre-computed eye-shape weights so the per-frame
  // lerp pulls toward them at the same EYE_SACCADE_SMOOTH rate as a normal
  // saccade — i.e. the gaze "snaps" naturally rather than teleporting.

  type GazeDirection =
    | 'forward' | 'down' | 'down_left' | 'down_right'
    | 'up' | 'up_left' | 'up_right' | 'left' | 'right' | 'away';
  let gazeOverride: GazeDirection | null = null;
  let gazeOverrideWeights: Record<string, number> = {};

  function gazeWeightsFor(direction: GazeDirection, intensity: number): Record<string, number> {
    if (direction === 'forward') return {};

    // 'away' is meta — pick a quadrant once. The figure stays on that
    // quadrant for as long as the override is active. Selecting per call
    // (rather than per frame) avoids the gaze jittering between quadrants.
    type ConcreteGaze = Exclude<GazeDirection, 'forward' | 'away'>;
    let dir: ConcreteGaze;
    if (direction === 'away') {
      const quadrants: ConcreteGaze[] = ['up_left', 'up_right', 'down_left', 'down_right'];
      dir = quadrants[Math.floor(Math.random() * quadrants.length)]!;
    } else {
      dir = direction;
    }

    // Slightly larger amplitude than a procedural saccade — a director-
    // scripted gaze is meant to *read*. intensity scales the amplitude so
    // a soft "look down" (intensity 0.4) is gentler than a hard one (1.0).
    const baseH = 0.50;
    const baseV = 0.40;
    const t = Math.max(0.3, Math.min(1, intensity)); // clamp so 0 doesn't hide it
    const h = baseH * t;
    const v = baseV * t;

    const wantsLeft = dir.includes('left');
    const wantsRight = dir.includes('right');
    const wantsUp = dir.startsWith('up');
    const wantsDown = dir.startsWith('down');

    return {
      // Convergence/divergence semantics for ARKit eye-look shapes:
      //   eyeLookOutLeft  + eyeLookInRight  → subject looks LEFT
      //   eyeLookInLeft   + eyeLookOutRight → subject looks RIGHT
      eyeLookInLeft:    wantsRight ? h : 0,
      eyeLookOutLeft:   wantsLeft  ? h : 0,
      eyeLookInRight:   wantsLeft  ? h : 0,
      eyeLookOutRight:  wantsRight ? h : 0,
      eyeLookUpLeft:    wantsUp    ? v : 0,
      eyeLookUpRight:   wantsUp    ? v : 0,
      eyeLookDownLeft:  wantsDown  ? v : 0,
      eyeLookDownRight: wantsDown  ? v : 0,
    };
  }

  function scheduleEyeSaccade(): void {
    if (!hasEyeSaccades) return;
    // Interval, fixation, amplitude and down-bias all come from the active
    // emotion profile. arousalScale shrinks intervals when energy is high.
    const intervalMs = rangePick(currentEyeProfile.saccadeIntervalMs, arousalScale);
    eyeSaccadeTimerId = setTimeout(() => {
      // Director-scripted gaze takes precedence. Skip this saccade and
      // re-arm; the override will continue to drive eyeTarget directly.
      if (gazeOverride !== null) {
        scheduleEyeSaccade();
        return;
      }
      const h = rangePick(currentEyeProfile.saccadeAmpH);
      const v = rangePick(currentEyeProfile.saccadeAmpV);
      const lookLeft = Math.random() < 0.5;
      const lookDown = Math.random() < currentEyeProfile.saccadeDownBias;
      eyeTarget = {
        eyeLookInLeft:    lookLeft ? 0 : h,
        eyeLookOutLeft:   lookLeft ? h : 0,
        eyeLookInRight:   lookLeft ? h : 0,
        eyeLookOutRight:  lookLeft ? 0 : h,
        eyeLookUpLeft:    lookDown ? 0 : v,
        eyeLookUpRight:   lookDown ? 0 : v,
        eyeLookDownLeft:  lookDown ? v : 0,
        eyeLookDownRight: lookDown ? v : 0,
      };
      // Fixate, then return to centre. arousalScale leaves fixation alone —
      // a panicked person makes more rapid moves but each fixation still
      // needs ≥150 ms to read as a real "look at" rather than a smear.
      // The fixation reset must NOT clobber an in-flight override.
      const fixationMs = rangePick(currentEyeProfile.saccadeFixationMs);
      setTimeout(() => {
        if (gazeOverride !== null) return;
        eyeTarget = {};
      }, fixationMs);
      scheduleEyeSaccade();
    }, intervalMs);
  }

  if (hasEyeSaccades) scheduleEyeSaccade();

  // ── Procedural gesture layer ────────────────────────────────────────────
  //
  // Time-bounded envelopes overlaying the emotion + procedural layers.
  // Each gesture is a single trigger that runs to completion (300 ms –
  // 1.5 s) and then auto-clears. Gestures contribute to:
  //   • blendshape weights via gestureShapeContribs (max-merged below)
  //   • head bone rotation via gestureHeadOffset (additive after noise)
  //
  // Why not lerp into them: the envelope (attack/hold/decay) already
  // shapes the time response. A second lerp on top would smear the
  // transient — the whole point of the gesture is the sharp impact.
  // The per-shape composition uses a fast lerp constant when a gesture
  // is the strongest contributor so it follows the envelope tightly.

  type GestureName =
    | 'gasp' | 'laugh' | 'chuckle' | 'wince' | 'flinch'
    | 'head_shake' | 'head_nod' | 'eyebrow_flash' | 'sigh' | 'yawn';

  interface GestureFrame {
    shapes?: Record<string, number>;
    head?: { x?: number; y?: number; z?: number };
  }

  interface GestureProfile {
    duration: number;
    sample(t: number): GestureFrame;
  }

  // Trapezoidal envelope: ramp 0→1 over `attack`, hold 1 for `hold`,
  // ramp 1→0 over `decay`. Returns 0 outside the window.
  const trapezoid = (t: number, attack: number, hold: number, decay: number): number => {
    if (t < 0) return 0;
    if (t < attack) return t / attack;
    if (t < attack + hold) return 1;
    if (t < attack + hold + decay) return 1 - (t - attack - hold) / decay;
    return 0;
  };

  // Damped oscillator: sine at `freq` Hz that fades out linearly between
  // `fadeStart` and `fadeEnd`. Used for laugh jaw bounces & head wobbles.
  const dampedOsc = (t: number, freq: number, fadeStart: number, fadeEnd: number): number => {
    if (t < 0 || t > fadeEnd) return 0;
    const env = t < fadeStart ? 1 : Math.max(0, 1 - (t - fadeStart) / (fadeEnd - fadeStart));
    return Math.sin(2 * Math.PI * freq * t) * env;
  };

  const GESTURE_PROFILES: Record<GestureName, GestureProfile> = {
    // Sharp impact: 80 ms attack, 100 ms hold, 400 ms decay. Jaw drops,
    // eyes pop wide, brows lift slightly. Lands the moment of shock.
    gasp: {
      duration: 0.6,
      sample(t) {
        const e = trapezoid(t, 0.08, 0.1, 0.4);
        return {
          shapes: {
            jawOpen: 0.7 * e,
            eyeWideLeft: 0.7 * e,
            eyeWideRight: 0.7 * e,
            browInnerUp: 0.4 * e,
          },
        };
      },
    },
    // Belly laugh: 100 ms smile attack → ~1 s sustained smile/squint →
    // 6 Hz jaw bounces layered on top during the second half. Pair with
    // emotion="joy" so the residual smile lingers.
    //
    // No head wobble: the previous ±2.8° y-axis wobble read as a "no"
    // headshake rather than a laugh, so it was removed. The jaw bounces
    // alone read as laughter without the false negation signal.
    laugh: {
      duration: 2.1,
      sample(t) {
        const smile = trapezoid(t, 0.15, 1.5, 0.45);
        // Bounces start ~300 ms in, fade out from 1.5 s
        const bounce = t > 0.3 ? Math.max(0, dampedOsc(t - 0.3, 3, 1.2, 1.8)) : 0;
        return {
          shapes: {
            mouthSmileLeft: 0.8 * smile,
            mouthSmileRight: 0.8 * smile,
            cheekSquintLeft: 0.5 * smile,
            cheekSquintRight: 0.5 * smile,
            eyeSquintLeft: 0.3 * smile,
            eyeSquintRight: 0.3 * smile,
            jawOpen: bounce * 0.3,
          },
        };
      },
    },
    // Softer single laugh — for self-deprecation and low-stakes amusement.
    // No jaw bounces, just one mouth pulse + slight cheek squint.
    chuckle: {
      duration: 0.7,
      sample(t) {
        const smile = trapezoid(t, 0.08, 0.3, 0.3);
        const jaw = Math.max(0, Math.sin(2 * Math.PI * 4 * t) * Math.max(0, 1 - t / 0.7));
        return {
          shapes: {
            mouthSmileLeft: 0.5 * smile,
            mouthSmileRight: 0.5 * smile,
            cheekSquintLeft: 0.3 * smile,
            cheekSquintRight: 0.3 * smile,
            jawOpen: jaw * 0.15,
          },
        };
      },
    },
    wince: {
      duration: 0.5,
      sample(t) {
        const e = trapezoid(t, 0.06, 0.15, 0.3);
        return {
          shapes: {
            eyeSquintLeft: 0.7 * e,
            eyeSquintRight: 0.7 * e,
            browDownLeft: 0.4 * e,
            browDownRight: 0.4 * e,
            mouthPressLeft: 0.3 * e,
            mouthPressRight: 0.3 * e,
          },
        };
      },
    },
    // Wince + head pull-back. Reflexive recoil from a perceived threat.
    flinch: {
      duration: 0.6,
      sample(t) {
        const e = trapezoid(t, 0.05, 0.1, 0.45);
        return {
          shapes: {
            eyeSquintLeft: 0.8 * e,
            eyeSquintRight: 0.8 * e,
            browDownLeft: 0.5 * e,
            browDownRight: 0.5 * e,
            mouthPressLeft: 0.4 * e,
            mouthPressRight: 0.4 * e,
          },
          head: { x: -0.14 * e }, // ~-8° (chin up = head back)
        };
      },
    },
    // ±15° lateral head oscillation at 3 Hz, two cycles, fade out.
    head_shake: {
      duration: 0.7,
      sample(t) {
        const env = t < 0.6 ? 1 : Math.max(0, 1 - (t - 0.6) / 0.1);
        const shake = Math.sin(2 * Math.PI * 3 * t) * env;
        return { head: { y: shake * 0.262 } }; // 15° = 0.262 rad
      },
    },
    // ±10° vertical head oscillation at 3 Hz, two cycles.
    head_nod: {
      duration: 0.7,
      sample(t) {
        const env = t < 0.6 ? 1 : Math.max(0, 1 - (t - 0.6) / 0.1);
        const nod = Math.sin(2 * Math.PI * 3 * t) * env;
        return { head: { x: nod * 0.175 } }; // 10° = 0.175 rad
      },
    },
    // Quick brow-up spike. Greeting / "ah, I see" recognition cue.
    eyebrow_flash: {
      duration: 0.4,
      sample(t) {
        const e = trapezoid(t, 0.08, 0.1, 0.2);
        return {
          shapes: {
            browOuterUpLeft: 0.7 * e,
            browOuterUpRight: 0.7 * e,
            browInnerUp: 0.3 * e,
          },
        };
      },
    },
    // Two-phase yawn:
    //   Phase 1 (0–0.7 s) — reflex/surprise: jaw drops, eyes pop WIDE,
    //     outer brows lift (the involuntary "oh, it's happening" look).
    //   Phase 2 (0.8–2.4 s) — effort/tear: eyes squeeze into a squint as
    //     the jaw holds open, inner brows strain, then everything closes.
    // eyeWide and eyeSquint are different shapes so MAX composition hands
    // off cleanly — wide fades out just as squint is building.
    // No head movement — the body clip already handles the chin-lift.
    yawn: {
      duration: 2.4,
      sample(t) {
        // Jaw: 0.8 s attack, 0.6 s hold, 0.8 s decay
        const jaw = trapezoid(t, 0.8, 0.6, 0.8) * 0.9;
        // Eyes wide — fast onset (0.3 s), brief hold, fades by t ≈ 1.0 s
        const wide = trapezoid(t, 0.3, 0.4, 0.5) * 0.65;
        // Outer brows up — pairs with wide eyes for the surprise read
        const browOuter = trapezoid(t, 0.3, 0.4, 0.6) * 0.5;
        // Squint — slow build (1.0 s attack), rides through peak, fades last
        const squint = trapezoid(t, 1.0, 0.6, 0.8) * 0.65;
        // Inner brow strain — peaks during jaw-hold, slower than outer brows
        const browInner = trapezoid(t, 0.6, 0.8, 0.6) * 0.35;
        return {
          shapes: {
            jawOpen: jaw,
            eyeWideLeft: wide,
            eyeWideRight: wide,
            browOuterUpLeft: browOuter,
            browOuterUpRight: browOuter,
            eyeSquintLeft: squint,
            eyeSquintRight: squint,
            browInnerUp: browInner,
          },
        };
      },
    },
    // Slow inhale (chin lifts) → heavy exhale (chin dips, brow furrows,
    // mouth corners pull down). The whole shape reads as resignation.
    sigh: {
      duration: 1.5,
      sample(t) {
        let jawO = 0, headX = 0, mouthFr = 0, browIn = 0;
        if (t < 0.5) {
          const phase = t / 0.5;
          jawO = 0.15 * Math.sin(phase * Math.PI);
          headX = -0.04 * Math.sin(phase * Math.PI); // chin up (head back)
        } else if (t < 1.5) {
          const phase = (t - 0.5) / 1.0;
          const dec = Math.sin(phase * Math.PI);
          jawO = 0.1 * dec;
          headX = 0.05 * dec; // chin down
          mouthFr = 0.2 * dec;
          browIn = 0.3 * dec;
        }
        return {
          shapes: {
            jawOpen: jawO,
            mouthFrownLeft: mouthFr,
            mouthFrownRight: mouthFr,
            browInnerUp: browIn,
          },
          head: { x: headX },
        };
      },
    },
  };

  let activeGesture: GestureName | null = null;
  let gestureClock = 0;
  let gestureShapeContribs: Record<string, number> = {};
  let gestureHeadOffset: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };
  // 50 ms lerp constant for gesture-winning shapes — fast enough that the
  // per-shape lerp doesn't smear the envelope's attack but smooth enough
  // not to look like a teleport when the gesture starts.
  const GESTURE_LERP_SEC = 0.05;

  function triggerGesture(name: string | null | undefined): void {
    if (name == null) return;
    if (!(name in GESTURE_PROFILES)) {
      console.warn(`[figure] unknown gesture: ${name}`);
      return;
    }
    activeGesture = name as GestureName;
    gestureClock = 0;
    console.log(`[figure] gesture fired: ${name}`);
  }

  // ── Procedural body system ──────────────────────────────────────────────
  //
  // Three independent layers, all summing into a single per-frame
  // accumulator (`bodyOffsetAccum`) that is applied to every resolved
  // bone slot at the end of update():
  //
  //   1. Body state (continuous): selected `currentBodyState` plus a
  //      lerped offset table that smooths transitions between states.
  //      e.g. switching `restless_idle` → `slumped_idle` ramps over
  //      BODY_STATE_LERP_SEC instead of snapping.
  //
  //   2. Body gesture (transient): a single in-flight body gesture with
  //      its own clock, profile sample, and auto-clear on duration. Runs
  //      on a slot independent of the face gesture so they layer.
  //
  //   3. Procedural holds (sustained, fade in/out): each active hold is
  //      a target pose dictionary with an envelope state machine —
  //      attack ramp on activation, full sustain while held, decay ramp
  //      on release. Multiple holds sum (rare in practice).
  //
  // The accumulator is rebuilt every frame from scratch, so there's no
  // residual-state risk: layers that turn off contribute zero next frame.

  let activeBodyGesture: BodyGesture | null = null;
  let bodyGestureClock = 0;

  // Smoothed body-state offsets. Switching states updates `bodyStateTarget`;
  // every frame `bodyStateCurrent` lerps toward it.
  let currentBodyState: BodyState = 'neutral_idle';
  let bodyStateCurrent: Record<BoneSlot, EulerOffset> = emptyOffsets();
  let bodyStateTarget: Record<BoneSlot, EulerOffset> = emptyOffsets();
  /** ~1.0 s exponential lerp between body states. */
  const BODY_STATE_LERP_SEC = 1.0;

  // ── Procedural hold envelope state machine ─────────────────────────────
  //
  // Each procedural hold is tracked by name with an envelope that runs:
  //   'attack'  → weight ramps 0 → baseWeight over PROC_HOLD_ATTACK_SEC
  //   'sustain' → weight stays at baseWeight until releaseHold is called
  //   'release' → weight ramps current → 0 over PROC_HOLD_RELEASE_SEC
  // After release fades to 0, the entry is removed from the map.
  //
  // The runtime sums each active hold's pose weighted by current envelope
  // amplitude, so re-applying `setHold` just refreshes baseWeight without
  // restarting the envelope.

  type ProcHoldPhase = 'attack' | 'sustain' | 'release';
  interface ProcHoldEntry {
    phase: ProcHoldPhase;
    weight: number;       // current envelope amplitude (0..baseWeight)
    baseWeight: number;   // target sustain weight
    elapsed: number;      // time within the current phase (s)
    /**
     * Optional per-hold duration overrides for the attack/release ramps.
     * When undefined, the envelope tick uses the module-level constants
     * PROC_HOLD_ATTACK_SEC / PROC_HOLD_RELEASE_SEC. Set per activation
     * via figure.setHold(name, url, weight, attackSec) or per release
     * via figure.releaseHold(name, releaseSec) so the director can
     * shape the FEELING of a transition (snap vs settle) without
     * touching the global defaults.
     */
    attackSec?: number;
    releaseSec?: number;
    /**
     * Optional easing family for the attack/release curves. When undefined,
     * the envelope falls back to the `smooth` family (ease-out cubic both
     * ways), matching the previous hardcoded behaviour. Set per activation
     * (via setHold) or overridden on release (via releaseHold) so that a
     * pose attacked with `gentle` can release with `snap` if a different
     * emotion drives its dismissal.
     */
    easing?: EasingFamily;
    /**
     * Per-claimed-bone anchor: the live mixer-driven rotation captured on
     * the first `update()` tick after activation, BEFORE any procedural
     * override is applied. The override formula uses (anchor + holdOffset)
     * as the lock target so hand-authored offsets — calibrated against the
     * mixer's idle pose — read correctly when the mixer's contribution is
     * faded out by the override.
     *
     * Lazy-captured: `null` on creation, populated on first apply tick.
     * Not refilled on subsequent activations of the same hold (returning
     * from release to attack), which keeps the lock pose stable across
     * weight oscillations. If the user wants a refreshed anchor (e.g. the
     * idle has drifted significantly), they can release and re-activate.
     */
    anchors: Partial<Record<BoneSlot, { x: number; y: number; z: number }>> | null;
  }
  const proceduralHolds = new Map<string, ProcHoldEntry>();
  const PROC_HOLD_ATTACK_SEC = 0.55;
  const PROC_HOLD_RELEASE_SEC = 0.5;

  function triggerBodyGesture(name: string | null | undefined): void {
    if (name == null) return;
    if (!(name in BODY_GESTURE_PROFILES)) {
      console.warn(`[figure] unknown body gesture: ${name}`);
      return;
    }
    activeBodyGesture = name as BodyGesture;
    bodyGestureClock = 0;
    console.log(`[figure] body gesture fired: ${name}`);
  }

  function setBodyState(name: string | null | undefined): void {
    const target: BodyState = (name && name in BODY_STATE_PROFILES
      ? (name as BodyState)
      : 'neutral_idle');
    if (target === currentBodyState) return;
    currentBodyState = target;
    // Recompute target offsets from the bias table only — the noise
    // contribution is added per frame on top of the lerped bias.
    bodyStateTarget = emptyOffsets();
    accumulatePose(bodyStateTarget, BODY_STATE_PROFILES[target].bias, 1);
    console.log(`[figure] body state → ${target}`);
  }

  /**
   * Activate a procedural hold by name. Idempotent: re-applying the same
   * name with a new weight just updates baseWeight in place. If the hold
   * was in 'release', it returns to 'attack' from the current weight.
   *
   * LIMB CONFLICT RESOLUTION: each hold claims one or both arm chains (see
   * PROCEDURAL_HOLD_LIMBS). Activating a hold automatically releases any
   * active hold that shares a limb — so `arms_crossed` + `hand_in_pocket_
   * right` cannot both be active at once: the second activation kicks the
   * first into release. This is safety by construction; the LLM is also
   * told to use one hold at a time but the runtime guarantees correctness
   * regardless.
   */
  function activateProceduralHold(
    name: string,
    baseWeight: number,
    attackSec?: number,
    easing?: EasingFamily,
  ): void {
    if (!isProceduralHold(name)) {
      console.warn(`[figure] activateProceduralHold called with unknown name: ${name}`);
      return;
    }
    const conflicts = conflictingHolds(name, proceduralHolds.keys());
    for (const conflict of conflicts) {
      console.log(`[figure] hold conflict: "${name}" claims overlapping limbs with "${conflict}" — releasing the older one`);
      releaseProceduralHold(conflict);
    }
    const existing = proceduralHolds.get(name);
    if (existing) {
      existing.baseWeight = baseWeight;
      if (existing.phase === 'release') {
        existing.phase = 'attack';
        existing.elapsed = 0;
        // Refresh attack timing / curve in case the new activation wants a
        // different feel from the previous one.
        existing.attackSec = attackSec;
        if (easing) existing.easing = easing;
      }
      return;
    }
    proceduralHolds.set(name, {
      phase: 'attack',
      weight: 0,
      baseWeight,
      elapsed: 0,
      attackSec,
      easing,
      anchors: null, // lazy-captured on first update() tick
    });
    console.log(`[figure] procedural hold activated: ${name} (weight=${baseWeight.toFixed(2)}${attackSec != null ? `, attack=${attackSec.toFixed(2)}s` : ''}${easing ? `, easing=${easing}` : ''})`);
  }

  function releaseProceduralHold(
    name: string,
    releaseSec?: number,
    easing?: EasingFamily,
  ): void {
    const entry = proceduralHolds.get(name);
    if (!entry) return;
    entry.phase = 'release';
    entry.elapsed = 0;
    if (releaseSec != null) entry.releaseSec = releaseSec;
    // A new emotion may dictate a different release curve from the one
    // used on attack (e.g. attacked under `gentle` sadness, released
    // under `snap` anger). When provided, override the family on the
    // existing entry so the release curve reflects the new feeling.
    if (easing) entry.easing = easing;
    console.log(`[figure] procedural hold releasing: ${name}${releaseSec != null ? ` (release=${releaseSec.toFixed(2)}s)` : ''}${easing ? ` (easing=${easing})` : ''}`);
  }

  for (const m of [...skinnedMeshes, ...plainMeshes]) {
    m.frustumCulled = false;
    m.castShadow = false;
    m.receiveShadow = false;
  }

  if (fillMaterial && wireMaterial) {
    for (const mesh of skinnedMeshes) {
      mesh.material = fillMaterial;

      // Sibling clone: shares the geometry and (after `.bind`) the same
      // skeleton, so animation playing through the mixer drives both
      // meshes identically. Re-binding with the source's `bindMatrix`
      // ensures the clone's vertex weights map onto the live skeleton at
      // exactly the same rest pose.
      const wireMesh = mesh.clone() as SkinnedMesh;
      wireMesh.material = wireMaterial;
      wireMesh.bind(mesh.skeleton, mesh.bindMatrix);
      wireMesh.frustumCulled = false;
      wireMesh.castShadow = false;
      wireMesh.receiveShadow = false;
      wireMesh.renderOrder = (mesh.renderOrder ?? 0) + 1;
      mesh.parent?.add(wireMesh);
    }
    // Plain (non-skinned) meshes — rare in Mixamo exports but possible
    // for accessories. Single fill material; no wireframe overlay (their
    // topology usually isn't worth showing).
    for (const mesh of plainMeshes) {
      mesh.material = fillMaterial;
    }
  }

  const mixer = new AnimationMixer(root);
  let currentAction: AnimationAction | null = null;

  // ── Time-scale lerping ────────────────────────────────────────────────
  //
  // Beats request a global animation speed (low energy → 0.6×, high energy
  // → 1.4×). A hard jump on every beat reads as a tempo "click" — the
  // character visibly speeds up or slows down at the boundary. We instead
  // hold a target and lerp toward it inside `update(dtSec)` over
  // ~TIME_SCALE_LERP_SEC.
  //
  // The applied value is `timeScaleCurrent`. `timeScaleTarget` is what
  // `setTimeScale(s)` sets. New clips inherit the *current* scale at
  // crossfade time (not the target) so the body's apparent speed is
  // continuous across clip boundaries.
  let timeScaleTarget = 1;
  let timeScaleCurrent = 1;
  const TIME_SCALE_LERP_SEC = 0.45;

  /**
   * Cache parsed clips by URL. After the first play, switching to the same
   * clip later is instant — no FBX re-parse, no skeleton walk. Memory cost
   * is negligible because the cache only keeps `AnimationClip` keyframe data.
   *
   * Two caches because of root-motion semantics:
   *   - `clipCache` holds the RAW clip (with hip-position track intact).
   *     Used by locomotion beats so the figure actually translates.
   *   - `lockedClipCache` holds a clone with `Hips.position` filtered out.
   *     Used by every non-locomotion clip so the figure plays in place.
   *
   * The same URL can appear in both caches when the BeatPlayer plays a
   * locomotion variant of a clip and a non-locomotion beat later requests
   * the same URL. Memory cost is negligible: a clone shares track data
   * arrays except the one we filtered out.
   */
  const clipCache = new Map<string, AnimationClip>();
  const lockedClipCache = new Map<string, AnimationClip>();
  // HY-Motion gestures that should keep the (anchored) hip-position track.
  // Cached by URL alone — same as lockedClipCache — because preserveHip is
  // always true for the same beat source (uthana_text). Value is the raw
  // clip from clipCache, not a clone, since no per-play mutation occurs.
  const preservedHipClipCache = new Map<string, AnimationClip>();

  /** Load + normalise the raw clip from FBX/GLB. Always locked-in-place=false. */
  async function fetchRawClip(clipUrl: string): Promise<AnimationClip> {
    const cached = clipCache.get(clipUrl);
    if (cached) return cached;

    const isGlb = /\.(glb|gltf)(\?|$)/i.test(clipUrl)
      || clipUrl.startsWith('data:model/gltf-binary')
      || clipUrl.startsWith('data:model/gltf+json');
    let clip: AnimationClip;

    if (isGlb) {
      // GLB animation-only files (e.g. from Uthana) store the clip in
      // gltf.animations[], not inside a scene object.
      let gltf: { animations: AnimationClip[] };
      try {
        gltf = await gltfLoader.loadAsync(clipUrl) as unknown as { animations: AnimationClip[] };
      } catch (err) {
        throw new Error(`GLTFLoader failed for "${clipUrl}": ${(err as Error).message}`);
      }
      const raw = gltf.animations[0];
      if (!raw) throw new Error(`GLB at ${clipUrl} contains no animations.`);
      clip = raw;
    } else {
      let obj: Group;
      try {
        obj = await fbxLoader.loadAsync(clipUrl);
      } catch (err) {
        throw new Error(`FBXLoader failed for "${clipUrl}": ${(err as Error).message}`);
      }
      const raw = obj.animations[0];
      if (!raw) throw new Error(`FBX at ${clipUrl} contains no animations.`);
      clip = raw;
    }

    // Two parallel pipelines:
    //
    // (a) Mixamo/Avaturn path — `ccRetargetCtx` is null. Track names need
    //     to match the runtime skeleton's `mixamorig`-prefixed bones, so
    //     `normalizeMixamoTracks` prepends the prefix to bare names.
    //
    // (b) cc-base path — `ccRetargetCtx` is set. We MUST NOT prepend the
    //     prefix: the retargeter's source skeleton is Lewis loaded directly
    //     (no normalization), which keeps Lewis's bare bone names (`Hips`,
    //     `Spine`, …). The HY-Motion clip also arrives with bare names. If
    //     we prepend, track-bone `mixamorigHips` won't match donor-bone
    //     `Hips`, `findIndexOfBoneByName` returns -1, every track is
    //     dropped, and the retargeted clip is empty (duration 0). Same
    //     pipeline as the validated test page.
    if (ccRetargetCtx) {
      clip = processClipForCcRig(clip, ccRetargetCtx);
    } else {
      normalizeMixamoTracks(clip);
      // Anchor the clip's Hips.position track to the runtime character's
      // bind hip pose (frame-0 = bind, subsequent frames keep their
      // original deltas). Required for HY-Motion gestures played via
      // preserveHip=true; harmless for library clips that get locked-in-
      // place or locomotion-rebased downstream. Cc-base goes through
      // processClipForCcRig which does its own (more elaborate) hip
      // anchor + world-space retarget — never apply both.
      anchorHipPositionToBind(clip, bindHipPosition);
    }

    clipCache.set(clipUrl, clip);
    return clip;
  }

  /**
   * Fetch a clip ready to play. When `lockInPlace` is true (the default),
   * returns a cached locked-in-place clone with the `Hips.position` track
   * stripped — the figure plays the clip on a treadmill. When false (used
   * for `uthana_locomotion` beats), returns the raw clip with the hip
   * track intact, so the figure translates through world space.
   *
   * The cached locked clone is keyed on URL alone. Two beats requesting
   * the same URL with the same lock setting share an action; mixed lock
   * settings on the same URL get separate actions (intentional — the
   * player wants distinct lifecycles).
   *
   * Diagnostic logging on the unlocked path: surfaces whether the source
   * clip actually contains a hip-position track. If a locomotion beat is
   * rendered but the clip ships in-place (no Hips.position), the figure
   * stays anchored despite our flag — and the operator needs to know.
   */
  // Track which raw clips we've already normalised for locomotion playback,
  // so re-fetching the same URL doesn't re-rebase the values (which would
  // collapse the motion to a single point on the second pass).
  // Keyed by `${url}:${strides}` so the same walk clip can be cached at
  // different stride counts without one mutating the other's track data.
  const normalisedLocomotionClips = new Map<string, AnimationClip>();

  async function fetchClip(
    clipUrl: string,
    lockInPlace: boolean,
    strides = 1,
    preserveHip = false,
  ): Promise<AnimationClip> {
    const raw = await fetchRawClip(clipUrl);
    // Preserve-hip path: HY-Motion gestures (uthana_text). The raw clip's
    // Hips.position has already been anchored to bind in fetchRawClip, so
    // returning it as-is gives lean / reach / crouch motion without the
    // levitation that an unanchored raw clip would produce.
    if (preserveHip) {
      const cached = preservedHipClipCache.get(clipUrl);
      if (cached) return cached;
      preservedHipClipCache.set(clipUrl, raw);
      return raw;
    }
    if (!lockInPlace) {
      // Normalise the hip track ONCE per URL+strides combination before
      // first locomotion play. Different stride counts get separate clones
      // so stitching one doesn't corrupt the others.
      const cacheKey = `${clipUrl}:${strides}`;
      const cached = normalisedLocomotionClips.get(cacheKey);
      if (cached) return cached;
      const loco = strides > 1 ? raw.clone() : raw;
      prepareLocomotionClip(loco, bindHipPosition, strides);
      normalisedLocomotionClips.set(cacheKey, loco);
      return loco;
    }
    const cached = lockedClipCache.get(clipUrl);
    if (cached) return cached;
    // .clone() shares immutable track data; only the tracks ARRAY is new,
    // so filtering one entry out doesn't affect the raw clip.
    const locked = raw.clone();
    lockClipInPlace(locked);
    lockedClipCache.set(clipUrl, locked);
    return locked;
  }

  let onClipStartCb: ((url: string) => void) | null = null;
  let onClipFinishedCb: (() => void) | null = null;

  function setOnClipStart(cb: ((url: string) => void) | null): void {
    onClipStartCb = cb;
  }

  function setOnClipFinished(cb: (() => void) | null): void {
    onClipFinishedCb = cb;
  }

  function getClipDuration(url: string): number | null {
    // Either cache will do — both variants share keyframe data and
    // therefore have identical .duration. Prefer locked (more common).
    return lockedClipCache.get(url)?.duration ?? clipCache.get(url)?.duration ?? null;
  }

  async function playClipFromUrl(
    clipUrl: string,
    fadeSec = 0.4,
    loop: boolean | 'pingpong' = true,
    lockInPlace = true,
    strides = 1,
    preserveHip = false,
  ): Promise<void> {
    // lockInPlace = true (default) strips the hip-position track so the
    // figure plays on a treadmill — every clip stays anchored at the
    // figure's root. Pass false for uthana_locomotion beats so the
    // figure translates through world space (the camera follows in
    // main.ts via figure.root world-position smoothing).
    // preserveHip = true keeps the (frame-0-anchored) hip track intact —
    // used for HY-Motion gestures so lean / reach / crouch motion isn't
    // forced into rotation-only when lockInPlace is also true.
    const clip = await fetchClip(clipUrl, lockInPlace, strides, preserveHip);
    const next = mixer.clipAction(clip);

    // Same action is already current. mixer.clipAction(clip) returns the same
    // cached AnimationAction for a given clip, so when the BeatPlayer bridges
    // a face_only beat back to the idle (single-URL pool), `next` IS the
    // running idle action. Calling reset() would teleport the idle's playback
    // time back to frame 0, which the user sees as a body+arms snap on every
    // face_only beat. Bail early — the action is already running correctly.
    if (next === currentAction) {
      next.timeScale = timeScaleCurrent;
      onClipStartCb?.(clipUrl);
      return;
    }

    // Inherit the current (not target) tempo so a beat that started a fade
    // toward a slower speed continues that fade smoothly across the clip
    // boundary instead of teleporting back up to its target speed.
    next.timeScale = timeScaleCurrent;
    next.enabled = true;
    next.setEffectiveWeight(1);
    next.reset();
    if (loop === 'pingpong') {
      next.setLoop(LoopPingPong, Infinity);
    } else if (loop) {
      next.setLoop(LoopRepeat, Infinity);
    } else {
      next.setLoop(LoopOnce, 1);
      next.clampWhenFinished = true; // hold last frame until next crossfade
    }
    next.play();

    if (currentAction && currentAction !== next) {
      next.crossFadeFrom(currentAction, fadeSec, false);
    }

    currentAction = next;
    onClipStartCb?.(clipUrl);
  }

  // ── Queue-at-loop-point machinery ─────────────────────────────────────
  //
  // When the user picks a clip from the dropdown while something is already
  // playing, we don't hard-cut or even crossfade immediately — we wait for
  // the current action to reach its natural transition point, then we fire
  // the crossfade. This makes back-to-back clips feel choreographed rather
  // than interrupted.
  //
  // Two transition points we listen for:
  //   `loop`     — fired every time a LoopRepeat action restarts. For
  //                looping idles this happens every ~1–4s. We trigger the
  //                crossfade here so the body is always back at its start
  //                pose when the switch happens.
  //   `finished` — fired when a LoopOnce action plays through. For
  //                one-shot clips (jumps, cheers) we wait for the end of
  //                the clip before switching in.
  //
  // The clip is prefetched on `queueClipFromUrl` so the FBX is already
  // in memory by the time the event fires — the crossfade is seamless.
  //
  // Last call wins: queuing a second clip before the first fires just
  // replaces the pending URL.

  let pendingClipUrl: string | null = null;

  // Three.js mixer event payloads are plain objects; cast to access .action.
  type MixerEvent = { type: string; action: AnimationAction };

  mixer.addEventListener('loop', (e) => {
    const ev = e as unknown as MixerEvent;
    if (ev.action !== currentAction || !pendingClipUrl) return;
    const url = pendingClipUrl;
    pendingClipUrl = null;
    playClipFromUrl(url, 0.4).catch((err) =>
      console.warn('[figure] queued clip failed on loop', err),
    );
  });

  mixer.addEventListener('finished', (e) => {
    const ev = e as unknown as MixerEvent;
    if (ev.action !== currentAction) return;
    if (pendingClipUrl) {
      const url = pendingClipUrl;
      pendingClipUrl = null;
      // Finished means the clip is at the end; start with a shorter fade
      // so the new clip doesn't blend with a held end pose for too long.
      playClipFromUrl(url, 0.25).catch((err) =>
        console.warn('[figure] queued clip failed on finished', err),
      );
      return;
    }
    // No pending dropdown clip — notify the beat player (if active) so it
    // can advance to the next beat in the queue.
    onClipFinishedCb?.();
  });

  function queueClipFromUrl(clipUrl: string): void {
    if (!currentAction) {
      // Nothing playing yet — start immediately, no point queueing.
      playClipFromUrl(clipUrl, 0).catch((err) =>
        console.warn('[figure] immediate play on empty queue failed', err),
      );
      return;
    }
    // Prefetch now so the FBX is cached by the time the event fires.
    // queueClipFromUrl always locks-in-place (it's used for natural-
    // transition idle swaps, never for locomotion). If we ever want
    // queued locomotion, thread a lockInPlace flag through the API.
    fetchClip(clipUrl, true).catch((err) =>
      console.warn('[figure] prefetch failed for queued clip', err),
    );
    pendingClipUrl = clipUrl;
  }

  /**
   * Additive layers live in their own Map keyed by source URL. Each entry
   * is an already-additive `AnimationAction` running on the shared mixer.
   * A second call with the same URL just nudges the weight; a call with a
   * new URL stacks another layer on top. We never call
   * `AnimationUtils.makeClipAdditive` on a cached `AnimationClip` from
   * `clipCache` — we always clone the source clip before mutating, so the
   * dropdown's `playClipFromUrl` path can still use the same FBX file as a
   * normal-blended one-shot without interference.
   */
  const additiveActions = new Map<string, AnimationAction>();

  // Hold layers: keyed by canonical hold name (e.g. "hand_on_chin").
  // Separate from additiveActions so holds can be addressed by name rather
  // than URL (the URL may change between sessions; the name is stable).
  const holdActions = new Map<string, AnimationAction>();

  async function playAdditiveClipFromUrl(
    clipUrl: string,
    weight = 0.4,
    opts: { dropTracksMatching?: RegExp } = {},
  ): Promise<AnimationAction> {
    // ── Cache key includes the track filter ───────────────────────────────
    //
    // The cache stores the already-prepped (normalized + locked + additive)
    // clip's action by URL. If a filter was applied to the prep, the cached
    // entry is fundamentally a different clip than an unfiltered prep of
    // the same URL — so a future unfiltered call must NOT reuse the
    // filtered cache entry. Suffixing the key with the filter source keeps
    // both variants independently cacheable.
    const cacheKey = opts.dropTracksMatching
      ? `${clipUrl}::drop=${opts.dropTracksMatching.source}/${opts.dropTracksMatching.flags}`
      : clipUrl;
    const existing = additiveActions.get(cacheKey);
    if (existing) {
      existing.setEffectiveWeight(weight);
      if (!existing.isRunning()) existing.play();
      return existing;
    }

    const isGlbAdditive = /\.(glb|gltf)(\?|$)/i.test(clipUrl);
    let sourceClip: AnimationClip;
    if (isGlbAdditive) {
      const gltf = await gltfLoader.loadAsync(clipUrl) as unknown as { animations: AnimationClip[] };
      const raw = gltf.animations[0];
      if (!raw) throw new Error(`GLB at ${clipUrl} contains no animations.`);
      sourceClip = raw;
    } else {
      const obj = await fbxLoader.loadAsync(clipUrl);
      const raw = obj.animations[0];
      if (!raw) throw new Error(`FBX at ${clipUrl} contains no animations.`);
      sourceClip = raw;
    }

    // Clone before mutating; normalize bare bone names; lock root motion;
    // optionally filter tracks; convert to additive in place.
    //
    // normalizeMixamoTracks fixes Uthana / DeepMotion clips that ship with
    // bare bone track names ("Neck.quaternion") rather than the prefixed
    // "mixamorigNeck.quaternion" that our runtime skeleton uses after
    // normalizeMixamoBones. Without this, every track on the additive clip
    // logs "PropertyBinding: No target node found" and the additive layer
    // contributes nothing — same fix as the main-clip path at L1405.
    //
    // The track filter (if provided) drops tracks BEFORE makeClipAdditive,
    // so the dropped tracks contribute zero to the runtime additive layer.
    // The breathing layer uses this to exclude arm tracks — see
    // BREATHING_DROP_ARMS_RE for the rationale.
    //
    // makeClipAdditive(clip, 0) treats every frame as a delta from frame 0,
    // so the additive contribution sums on top of the main action without
    // the additive's absolute pose ever fighting the main pose.
    // Same two-pipeline rule as fetchRawClip — cc-base path skips the
    // `mixamorig` prefix injection because the retargeter's donor skeleton
    // (Lewis loaded directly) keeps bare bone names. Mixing them would
    // make the retargeter find no matching bones and emit an empty clip.
    let clip = sourceClip.clone();
    if (ccRetargetCtx) {
      clip = processClipForCcRig(clip, ccRetargetCtx);
    } else {
      normalizeMixamoTracks(clip);
    }
    lockClipInPlace(clip);
    if (opts.dropTracksMatching) {
      const before = clip.tracks.length;
      const filter = opts.dropTracksMatching;
      clip.tracks = clip.tracks.filter((t) => !filter.test(t.name));
      const dropped = before - clip.tracks.length;
      if (dropped > 0) {
        console.log(
          `[additive] dropped ${dropped}/${before} tracks matching ${filter} from ${clipUrl}`,
        );
      }
    }
    AnimationUtils.makeClipAdditive(clip, 0);

    const action = mixer.clipAction(clip, undefined, AdditiveAnimationBlendMode);
    action.setLoop(LoopRepeat, Infinity);
    action.setEffectiveWeight(weight);
    action.enabled = true;
    action.play();
    additiveActions.set(cacheKey, action);
    return action;
  }

  // ── Breathing layer (dedicated slot) ──────────────────────────────────
  //
  // The breathing layer is *one specific* additive action with two-axis
  // runtime control (bpm + amplitude). We hold a direct reference so the
  // update loop can write timeScale + weight every frame without looking
  // it up. Other additive layers go through `playAdditiveClipFromUrl`.
  //
  // INTRINSIC_BPM is the cadence of `Standing Idle.fbx` at timeScale=1.0;
  // Mixamo authors that loop at ~4s, ≈ 15 BPM. If you swap to a different
  // breath clip with a different cadence, override this via the (currently
  // private) constant — exposing it in the public API isn't worth the
  // surface area until the LLM director needs it.

  const INTRINSIC_BPM = 15;
  const BPM_MIN = 4;
  const BPM_MAX = 50;
  const AMP_MIN = 0;
  const AMP_MAX = 1;
  /** Time constant for breath state interpolation. ~63% convergence at this elapsed time. */
  const BREATH_SMOOTH_SEC = 1.5;

  // ── Breathing arm-track filter ───────────────────────────────────────────
  //
  // Mixamo's "Standing Idle" — the clip we use as the breathing additive
  // source — animates the entire body, arms included. Layered as additive
  // on top of the looping base idle (also Standing Idle), this doubles the
  // arm sway: held poses like `arms_crossed` then look like they're floating
  // because the breath layer keeps moving the arms underneath.
  //
  // This regex removes the additive contribution from the arm chain
  // (clavicle → upper arm → forearm → wrist/palm) on both sides. Spine,
  // clavicle-y chest rise, and neck breathing motion are preserved.
  // Finger sub-bones (LeftHandIndex1, etc.) are NOT matched — the regex
  // anchors with `\.` after `Hand`, so `LeftHandIndex1.quaternion` slides
  // through. Keeping fingers means the hands still get the small life from
  // the additive layer that prevents them reading as frozen.
  const BREATHING_DROP_ARMS_RE =
    /^(?:mixamorig:?)?(?:Left|Right)(?:Shoulder|Arm|ForeArm|Hand)\./i;

  let breathingAction: AnimationAction | null = null;
  let breathingClipUrl: string | null = null;
  const breathTarget: BreathState = { bpm: 15, amplitude: 0.4 };
  const breathCurrent: BreathState = { ...breathTarget };

  function clamp(v: number, lo: number, hi: number): number {
    return v < lo ? lo : v > hi ? hi : v;
  }

  async function setBreathing(opts: SetBreathingOpts): Promise<void> {
    if (typeof opts.bpm === 'number' && Number.isFinite(opts.bpm)) {
      breathTarget.bpm = clamp(opts.bpm, BPM_MIN, BPM_MAX);
    }
    if (
      typeof opts.amplitude === 'number' &&
      Number.isFinite(opts.amplitude)
    ) {
      breathTarget.amplitude = clamp(opts.amplitude, AMP_MIN, AMP_MAX);
    }

    if (opts.url && opts.url !== breathingClipUrl) {
      // Tear down the previous slot if any. We stop and forget — the action
      // stays in the mixer briefly until garbage-collected; that's fine
      // because `setEffectiveWeight(0)` plus `stop()` keeps it inert.
      if (breathingAction) {
        breathingAction.setEffectiveWeight(0);
        breathingAction.stop();
        if (breathingClipUrl) additiveActions.delete(breathingClipUrl);
      }
      breathingAction = await playAdditiveClipFromUrl(
        opts.url,
        breathCurrent.amplitude,
        { dropTracksMatching: BREATHING_DROP_ARMS_RE },
      );
      breathingClipUrl = opts.url;
    }
  }

  function getBreathingState(): BreathState {
    return { bpm: breathCurrent.bpm, amplitude: breathCurrent.amplitude };
  }

  // ── Hold layer management ─────────────────────────────────────────────
  //
  // Hold layers are named additive animation layers that persist across
  // beats. They are activated by name (e.g. "hand_on_chin") and use the
  // same additive mechanism as breathing — each hold clip is converted to
  // additive (delta from frame 0) and looped indefinitely.

  async function setHold(
    name: string,
    url: string,
    weight = 0.8,
    attackSec?: number,
    easing?: EasingFamily,
  ): Promise<void> {
    // ── Procedural dispatch ────────────────────────────────────────────────
    // Hold names backed by hand-authored bone poses (PROCEDURAL_HOLD_POSES
    // in body-profiles.ts) are activated as procedural envelopes — no GLB
    // load, no Uthana credit, no animation track binding. They run through
    // the body-bone accumulator alongside body gestures and body states.
    if (isProceduralHold(name)) {
      activateProceduralHold(name, weight, attackSec, easing);
      return;
    }

    // ── GLB additive backend (legacy / future Uthana holds) ────────────────
    const existing = holdActions.get(name);
    if (existing) {
      existing.setEffectiveWeight(weight);
      if (!existing.isRunning()) existing.play();
      return;
    }

    const action = await playAdditiveClipFromUrl(url, 0);
    action.setEffectiveWeight(0);
    // Attack defaults to 0.5s but the director can request faster
    // (snap into pose) or slower (gradual settle). Clamped to a safe
    // range to avoid runaway intervals.
    const FADE_IN_SEC = Math.max(
      0.05,
      Math.min(5, attackSec ?? 0.5),
    );
    const STEPS = 10;
    const stepMs = (FADE_IN_SEC / STEPS) * 1000;
    let step = 0;
    const fadeTimer = setInterval(() => {
      step++;
      const w = (step / STEPS) * weight;
      action.setEffectiveWeight(Math.min(w, weight));
      if (step >= STEPS) clearInterval(fadeTimer);
    }, stepMs);

    holdActions.set(name, action);
  }

  function releaseHold(
    name: string,
    fadeSec?: number,
    easing?: EasingFamily,
  ): void {
    // Clamp the override to the same safe range the validator uses
    // (0.05–5s). Default 0.5s when omitted, matching the original
    // behaviour and the ProcHoldEntry's PROC_HOLD_RELEASE_SEC fallback.
    const safeFade = fadeSec != null
      ? Math.max(0.05, Math.min(5, fadeSec))
      : 0.5;
    // Try the procedural hold map first; fall through to GLB hold map.
    if (proceduralHolds.has(name)) {
      // Pass through only when the caller specified a fade — otherwise
      // the procedural envelope keeps using its internal default
      // (PROC_HOLD_RELEASE_SEC), which matches pre-override behaviour.
      // Easing override (when given) replaces whatever family the hold
      // was running under: a hold attacked under sadness can release
      // under anger if the dismissing emotion is anger.
      releaseProceduralHold(
        name,
        fadeSec != null ? safeFade : undefined,
        easing,
      );
      return;
    }
    const action = holdActions.get(name);
    if (!action) return;
    action.fadeOut(safeFade);
    holdActions.delete(name);
    setTimeout(() => {
      if (action.isRunning()) action.stop();
    }, safeFade * 1000 + 150);
  }

  function releaseAllHolds(fadeSec = 0.5): void {
    for (const name of [...proceduralHolds.keys()]) {
      releaseProceduralHold(name);
    }
    for (const name of [...holdActions.keys()]) {
      releaseHold(name, fadeSec);
    }
  }

  function stop(): void {
    if (currentAction) {
      currentAction.stop();
      currentAction = null;
    }
  }

  function teardown(): void {
    mixer.stopAllAction();
    mixer.uncacheRoot(root);
    breathingAction = null;
    breathingClipUrl = null;
    currentAction = null;
    additiveActions.clear();
    holdActions.clear();
    clipCache.clear();
    // Cancel any pending blink timers so they don't fire after character swap
    if (blinkTimerId !== null) {
      clearTimeout(blinkTimerId);
      blinkTimerId = null;
    }
    if (eyeSaccadeTimerId !== null) {
      clearTimeout(eyeSaccadeTimerId);
      eyeSaccadeTimerId = null;
    }
    if (browTwitchTimerId !== null) {
      clearTimeout(browTwitchTimerId);
      browTwitchTimerId = null;
    }
    // Clear any in-flight gesture so a new character doesn't inherit
    // a half-finished envelope. The next beat's triggerGesture() (if any)
    // will start fresh.
    activeGesture = null;
    gestureClock = 0;
    gestureShapeContribs = {};
    gestureHeadOffset = { x: 0, y: 0, z: 0 };
    // Procedural body system: clear gesture, holds, and reset body state to
    // neutral. The lerp will ramp the new character toward neutral on
    // first frame instead of inheriting bias from the previous figure.
    activeBodyGesture = null;
    bodyGestureClock = 0;
    proceduralHolds.clear();
    currentBodyState = 'neutral_idle';
    bodyStateCurrent = emptyOffsets();
    bodyStateTarget = emptyOffsets();
  }

  function update(dtSec: number): void {
    // ── Procedural-noise restore (must run BEFORE mixer.update) ──────────
    // Restore head/hips to the clean baseline we captured last frame, BEFORE
    // we added our procedural offset. If the mixer is about to overwrite this
    // bone in its update() call, our restore is harmless. If the mixer is
    // NOT going to touch this bone (clip has no track for it), our restore
    // strips out the previous frame's offset so the new offset replaces it
    // rather than accumulating. See comment block at the bone declarations.
    if (headBone && headSaved) {
      const h = headBone as import('three').Object3D;
      h.rotation.x = headSaved.x;
      h.rotation.y = headSaved.y;
      h.rotation.z = headSaved.z;
    }
    if (hipsBone && hipsSaved) {
      const hp = hipsBone as import('three').Object3D;
      hp.position.x = hipsSaved.x;
      hp.position.y = hipsSaved.y;
      hp.position.z = hipsSaved.z;
    }
    // Restore body bones — same pattern as head/hips. Skips the head bone
    // because it's already restored above (we can't double-restore: that
    // would clobber the head's already-restored value with stale data).
    for (const slot of ALL_BONE_SLOTS) {
      const bone = bonesBySlot[slot];
      const saved = boneSavedRotations[slot];
      if (!bone || !saved) continue;
      // Note: hips position is handled separately above. The hips
      // ROTATION track (yaw for weight shift, etc.) is restored here.
      bone.rotation.x = saved.x;
      bone.rotation.y = saved.y;
      bone.rotation.z = saved.z;
    }

    mixer.update(dtSec);

    // ── Time-scale lerp ──────────────────────────────────────────────────
    // Exponential smoothing toward the beat-driven target. A new beat may
    // call setTimeScale at any moment; we close ~63% of the remaining gap
    // every TIME_SCALE_LERP_SEC. Frame-rate independent.
    if (timeScaleCurrent !== timeScaleTarget) {
      const tk = 1 - Math.exp(-dtSec / TIME_SCALE_LERP_SEC);
      timeScaleCurrent += (timeScaleTarget - timeScaleCurrent) * tk;
      // Snap to target when we're within 0.001 to avoid float drift.
      if (Math.abs(timeScaleCurrent - timeScaleTarget) < 0.001) {
        timeScaleCurrent = timeScaleTarget;
      }
      if (currentAction) currentAction.timeScale = timeScaleCurrent;
    }

    // ── Capture clean baseline (mixer-set or untouched) ───────────────────
    if (headBone) {
      const h = headBone as import('three').Object3D;
      headSaved = { x: h.rotation.x, y: h.rotation.y, z: h.rotation.z };
    }
    if (hipsBone) {
      const hp = hipsBone as import('three').Object3D;
      hipsSaved = { x: hp.position.x, y: hp.position.y, z: hp.position.z };
    }
    for (const slot of ALL_BONE_SLOTS) {
      const bone = bonesBySlot[slot];
      if (!bone) continue;
      boneSavedRotations[slot] = {
        x: bone.rotation.x,
        y: bone.rotation.y,
        z: bone.rotation.z,
      };
    }

    // ── Gesture tick ──────────────────────────────────────────────────────
    // Advance the active gesture's clock and sample its envelope. When the
    // gesture's duration is exceeded, clear the state so the per-shape max
    // composition naturally drops gesture contributions back to zero (and
    // the per-shape lerp eases the residual back to the underlying emotion).
    if (activeGesture) {
      gestureClock += dtSec;
      const profile = GESTURE_PROFILES[activeGesture];
      if (gestureClock >= profile.duration) {
        activeGesture = null;
        gestureShapeContribs = {};
        gestureHeadOffset = { x: 0, y: 0, z: 0 };
      } else {
        const frame = profile.sample(gestureClock);
        gestureShapeContribs = frame.shapes ?? {};
        gestureHeadOffset = {
          x: frame.head?.x ?? 0,
          y: frame.head?.y ?? 0,
          z: frame.head?.z ?? 0,
        };
      }
    }

    // ── Body gesture tick ─────────────────────────────────────────────────
    // Independent slot from the face gesture — the two layer cleanly so a
    // gasp face + recoil_step body can fire on the same beat. Auto-clears
    // when the profile's duration is exceeded.
    let bodyGestureFrameBones: ReturnType<
      typeof BODY_GESTURE_PROFILES[BodyGesture]['sample']
    >['bones'] | undefined;
    if (activeBodyGesture) {
      bodyGestureClock += dtSec;
      const profile = BODY_GESTURE_PROFILES[activeBodyGesture];
      if (bodyGestureClock >= profile.duration) {
        activeBodyGesture = null;
      } else {
        const frame = profile.sample(bodyGestureClock);
        bodyGestureFrameBones = frame.bones;
      }
    }

    // ── Procedural hold envelopes ─────────────────────────────────────────
    // Tick each active hold's phase. Attack ramps weight 0 → baseWeight;
    // sustain holds it; release ramps baseWeight → 0 then removes the entry.
    //
    // The curve shape is picked from the hold's easing family (set when
    // the hold was activated, optionally overridden on release). See
    // `easing.ts` for the four families and their feel:
    //   gentle  — soft both ways (sadness / calm)
    //   smooth  — naturalistic both ways (neutral / joy)
    //   snap    — decisive S-curve both ways (anger / excitement)
    //   jolt    — instant attack, gentle release (fear / surprise)
    for (const [name, entry] of [...proceduralHolds.entries()]) {
      entry.elapsed += dtSec;
      const fam = entry.easing && entry.easing in EASING
        ? EASING[entry.easing]
        : EASING.smooth;
      if (entry.phase === 'attack') {
        const dur = entry.attackSec ?? PROC_HOLD_ATTACK_SEC;
        const tNorm = Math.min(1, entry.elapsed / dur);
        entry.weight = entry.baseWeight * fam.attack(tNorm);
        if (tNorm >= 1) {
          entry.phase = 'sustain';
          entry.elapsed = 0;
          entry.weight = entry.baseWeight;
        }
      } else if (entry.phase === 'release') {
        const dur = entry.releaseSec ?? PROC_HOLD_RELEASE_SEC;
        const tNorm = Math.min(1, entry.elapsed / dur);
        // The release curve is expressed in `easing.ts` as a 0→1 falloff;
        // weight is `baseWeight * (1 - falloff)` so that t=0 gives full
        // weight and t=1 gives zero, matching the previous semantics.
        entry.weight = entry.baseWeight * (1 - fam.release(tNorm));
        if (tNorm >= 1) {
          proceduralHolds.delete(name);
        }
      }
    }

    // ── Body state lerp ───────────────────────────────────────────────────
    // Smoothly approach the target body-state bias offsets. Switching
    // states is a soft transition; transient gestures remain layered on
    // top via the accumulator below.
    {
      const k = 1 - Math.exp(-dtSec / BODY_STATE_LERP_SEC);
      for (const slot of ALL_BONE_SLOTS) {
        const cur = bodyStateCurrent[slot]!;
        const tgt = bodyStateTarget[slot]!;
        cur.x = (cur.x ?? 0) + (((tgt.x ?? 0) - (cur.x ?? 0)) * k);
        cur.y = (cur.y ?? 0) + (((tgt.y ?? 0) - (cur.y ?? 0)) * k);
        cur.z = (cur.z ?? 0) + (((tgt.z ?? 0) - (cur.z ?? 0)) * k);
      }
    }

    // Exponential smoothing toward the target. The constant `k` is the
    // fraction of the gap closed in one tick — independent of frame rate
    // because we derive it from `dtSec`.
    if (breathingAction) {
      const k = 1 - Math.exp(-dtSec / BREATH_SMOOTH_SEC);
      breathCurrent.bpm += (breathTarget.bpm - breathCurrent.bpm) * k;
      breathCurrent.amplitude +=
        (breathTarget.amplitude - breathCurrent.amplitude) * k;
      breathingAction.setEffectiveWeight(breathCurrent.amplitude);
      breathingAction.timeScale = Math.max(
        0.01,
        breathCurrent.bpm / INTRINSIC_BPM,
      );
    }

    // ── Head sway (sum-of-sines, irrational frequency ratios) ─────────────
    // Sub-degree non-repeating drift on three axes. The frequency ratios
    // (0.13, 0.23, 0.31, 0.71 …) are deliberately irrational so the
    // composite signal never exactly repeats within any practical playback
    // duration — looks like Perlin noise without the lookup tables.
    if (headBone) {
      const t = performance.now() / 1000;
      const h = headBone as import('three').Object3D;
      // ±0.06 rad ≈ ±3.5° at peak — visibly alive but not cartoonish
      const yaw =
        Math.sin(t * 0.23) * 0.045 +
        Math.sin(t * 0.71 + 1.7) * 0.020 +
        Math.sin(t * 0.13 + 3.4) * 0.012;
      const tilt =
        Math.sin(t * 0.17 + 0.9) * 0.030 +
        Math.sin(t * 0.43 + 2.1) * 0.014;
      const pitch =
        Math.sin(t * 0.11 + 1.4) * 0.022 +
        Math.sin(t * 0.31 + 4.2) * 0.010;
      h.rotation.y += yaw;
      h.rotation.z += tilt;
      h.rotation.x += pitch;
      // Gesture head offset (head_shake/nod/flinch/sigh) — additive on top
      // of the procedural noise so a head shake still has subtle micro-sway.
      if (activeGesture) {
        h.rotation.x += gestureHeadOffset.x;
        h.rotation.y += gestureHeadOffset.y;
        h.rotation.z += gestureHeadOffset.z;
      }
    }

    // ── Hips drift (asymmetric centre-of-mass shift) ──────────────────────
    // Tiny horizontal drift, ±1.5 cm. The frequencies are different from
    // the head's so the two layers never sync into a single rhythm. A slow
    // bias term shifts the centre of mass toward one foot for ~10s, then
    // gently swings to the other — mimicking natural weight transfer.
    if (hipsBone) {
      const t = performance.now() / 1000;
      const hp = hipsBone as import('three').Object3D;
      // Lateral (X): weight transfer + micro-sway, scaled to figure.scale (m)
      const lateral =
        Math.sin(t * 0.083 + 0.5) * 0.012 +
        Math.sin(t * 0.29 + 1.9) * 0.004;
      // Anterior-posterior (Z): smaller — people sway more left/right than fore/aft
      const sagittal =
        Math.sin(t * 0.11 + 2.3) * 0.005 +
        Math.sin(t * 0.37 + 0.7) * 0.002;
      // Vertical (Y): very small bob coupled to slower frequency (knees subtly bend)
      const vertical = Math.sin(t * 0.19 + 1.1) * 0.003;
      hp.position.x += lateral;
      hp.position.z += sagittal;
      hp.position.y += vertical;
    }

    // ── Compose body bone offsets (state + gesture + procedural holds) ────
    //
    // Two distinct apply paths share this block:
    //
    //   Path A (default):  bone = mixer + state + gesture
    //     For every bone NOT claimed by an active procedural hold. The
    //     original additive layering that lets idle motion + state bias +
    //     transient gestures stack naturally on top of the looping clip.
    //
    //   Path B (override): bone = lerp(mixer, anchor + hold) + state + gesture
    //     For bones a procedural hold currently claims (e.g. the four
    //     left-arm bones during `arms_crossed`). The crossfade weight W is
    //     the hold's envelope amplitude (0 → 1 over attack, 1 during
    //     sustain, 1 → 0 over release). At W=0 the formula reduces to
    //     mixer + state + gesture; at W=1 it becomes anchor + hold + state
    //     + gesture, fully replacing the mixer's contribution.
    //
    // The anchor is the bone's mixer-driven rotation captured on the first
    // tick after activation (lazily, below — boneSavedRotations holds the
    // post-mixer pose for this frame). Anchoring to the *current* mixer
    // pose rather than to bind/T-pose means hand-authored offsets, which
    // are calibrated as deltas from the idle pose, read correctly when the
    // override is at full strength. See the chat-log diagnosis around
    // "looks closer to T-pose" for why we don't use bind here.
    //
    // For multiple holds claiming the same slot (transient state during a
    // dedup handoff), the per-slot lock target is the weight-weighted mean
    // of each hold's `(anchor + offset)`. With single-hold steady state
    // this reduces to the trivial case.
    //
    // Body state and gesture offsets always pass through additively (they
    // are intentionally additive; `shoulder_shrug` should still read while
    // a hold is active). Holds own postural rotation for their claimed
    // bones — that's the whole point of a hold.
    {
      // ── Lazy anchor capture for any hold whose anchors are still null.
      // boneSavedRotations[slot] is the live mixer-driven rotation captured
      // earlier this frame, which is exactly the pre-override reference we
      // want as the lock target.
      if (proceduralHolds.size > 0) {
        for (const [name, entry] of proceduralHolds.entries()) {
          if (entry.anchors !== null) continue;
          const limbs = PROCEDURAL_HOLD_LIMBS[name as ProceduralHoldName];
          const captured: Partial<Record<BoneSlot, { x: number; y: number; z: number }>> = {};
          if (limbs) {
            for (const limb of limbs) {
              for (const slot of LIMB_TO_BONES[limb]) {
                const saved = boneSavedRotations[slot];
                if (!saved) continue;
                captured[slot] = { x: saved.x, y: saved.y, z: saved.z };
              }
            }
          }
          entry.anchors = captured;
        }
      }

      const accum = emptyOffsets();

      // Layer 1: lerped body state bias (already a per-slot table)
      for (const slot of ALL_BONE_SLOTS) {
        const o = bodyStateCurrent[slot]!;
        const a = accum[slot]!;
        a.x = (a.x ?? 0) + (o.x ?? 0);
        a.y = (a.y ?? 0) + (o.y ?? 0);
        a.z = (a.z ?? 0) + (o.z ?? 0);
      }

      // Layer 2: body state noise (sampled fresh per frame)
      const stateNoise = BODY_STATE_PROFILES[currentBodyState].noise;
      if (stateNoise) {
        accumulatePose(accum, stateNoise(performance.now() / 1000), 1);
      }

      // Layer 3: active body gesture
      if (bodyGestureFrameBones) {
        accumulatePose(accum, bodyGestureFrameBones, 1);
      }

      // Layer 4: procedural holds — separate from `accum` because they go
      // through the override path. We accumulate, per claimed slot, two
      // numbers:
      //
      //   targetWeighted[slot]  = Σ envelope_weight · (anchor + offset)
      //   claimedW[slot]        = Σ envelope_weight
      //
      // Then per-slot lock target = targetWeighted / claimedW (a weighted
      // mean of each hold's lock pose, weighted by current envelope).
      // The override crossfade uses min(1, claimedW) as the mix toward
      // that mean; with a single full-envelope hold this is just (anchor
      // + offset) at weight 1, the steady-state case.
      const targetWeighted = emptyOffsets();
      const claimedW: Partial<Record<BoneSlot, number>> = {};
      if (proceduralHolds.size > 0) {
        for (const [name, entry] of proceduralHolds.entries()) {
          const pose = PROCEDURAL_HOLD_POSES[name as keyof typeof PROCEDURAL_HOLD_POSES];
          if (!pose) continue;
          const limbs = PROCEDURAL_HOLD_LIMBS[name as ProceduralHoldName];
          if (!limbs) continue;
          const anchors = entry.anchors;
          if (!anchors) continue; // not yet captured (shouldn't happen — capture loop runs above)
          const w = entry.weight;
          if (w <= 0) continue;
          for (const limb of limbs) {
            for (const slot of LIMB_TO_BONES[limb]) {
              const anchor = anchors[slot];
              if (!anchor) continue; // bone wasn't resolvable; skip
              const offset = pose[slot] ?? {};
              const tx = (targetWeighted[slot]!.x ?? 0)
                + ((anchor.x + (offset.x ?? 0)) * w);
              const ty = (targetWeighted[slot]!.y ?? 0)
                + ((anchor.y + (offset.y ?? 0)) * w);
              const tz = (targetWeighted[slot]!.z ?? 0)
                + ((anchor.z + (offset.z ?? 0)) * w);
              targetWeighted[slot]!.x = tx;
              targetWeighted[slot]!.y = ty;
              targetWeighted[slot]!.z = tz;
              claimedW[slot] = (claimedW[slot] ?? 0) + w;
            }
          }
        }
      }

      // Apply per slot — pick path A or B based on claim weight.
      for (const slot of ALL_BONE_SLOTS) {
        const bone = bonesBySlot[slot];
        if (!bone) continue;
        const a = accum[slot]!;
        const w = claimedW[slot] ?? 0;
        if (w > 0) {
          // Path B — override. Lock target = weighted mean of all active
          // holds' (anchor + offset) on this slot. Crossfade weight is
          // clamped to 1 so a transient over-claim during a dedup handoff
          // doesn't push past the lock pose.
          const W = Math.min(1, w);
          const t = targetWeighted[slot]!;
          const avgX = (t.x ?? 0) / w;
          const avgY = (t.y ?? 0) / w;
          const avgZ = (t.z ?? 0) / w;
          const oneMinusW = 1 - W;
          bone.rotation.x = oneMinusW * bone.rotation.x + W * avgX + (a.x ?? 0);
          bone.rotation.y = oneMinusW * bone.rotation.y + W * avgY + (a.y ?? 0);
          bone.rotation.z = oneMinusW * bone.rotation.z + W * avgZ + (a.z ?? 0);
        } else {
          // Path A — default additive: bone = mixer + state + gesture.
          bone.rotation.x += a.x ?? 0;
          bone.rotation.y += a.y ?? 0;
          bone.rotation.z += a.z ?? 0;
        }
      }
    }

    // ── Eye saccades ───────────────────────────────────────────────────────
    if (hasEyeSaccades && morphMeshes.length > 0) {
      const eyeK = 1 - Math.exp(-dtSec / EYE_SACCADE_SMOOTH);
      for (const shape of EYE_SACCADE_SHAPES) {
        const target = eyeTarget[shape] ?? 0;
        eyeCurrent[shape] = (eyeCurrent[shape] ?? 0) + (target - (eyeCurrent[shape] ?? 0)) * eyeK;
      }
    }

    // ── Layered facial blendshape lerp ────────────────────────────────────
    //
    // Final per-shape target = max(ambient, beat, viseme, brow-twitch, blink,
    // saccade, gesture) so every layer can contribute and the strongest weight
    // wins. The viseme layer is gated to lip shapes only (`isLipShape`) so it
    // can never accidentally drive a brow / eye / cheek morph.
    if (morphMeshes.length > 0) {
      const beatK    = 1 - Math.exp(-dtSec / EXPRESSION_SMOOTH_SEC);
      const ambientK = 1 - Math.exp(-dtSec / AMBIENT_LERP_SEC);
      const visemeK  = 1 - Math.exp(-dtSec / VISEME_LERP_SEC);
      const blinkK   = 1 - Math.exp(-dtSec / BLINK_LERP_SEC);
      const browK    = 1 - Math.exp(-dtSec / BROW_LERP_SEC);

      // Update procedural scalars / per-shape lerped values
      blinkValue += (blinkTarget - blinkValue) * blinkK;
      for (const name of BROW_SHAPE_NAMES) {
        const tgt = browTwitchTarget[name] ?? 0;
        browTwitchValue[name] = (browTwitchValue[name] ?? 0) + (tgt - (browTwitchValue[name] ?? 0)) * browK;
      }

      for (const mesh of morphMeshes) {
        const influences = mesh.morphTargetInfluences;
        if (!influences) continue;
        const rev = morphIndexToName.get(mesh);
        if (!rev) continue;
        for (let i = 0; i < influences.length; i++) {
          const name = rev.get(i) ?? '';
          // Resolve canonical ARKit name once per shape so every layer
          // lookup below can fall through `actual → canonical`. This is
          // what makes a single layer write keyed canonical (`mouthSmileLeft`)
          // reach an Avaturn camelCase mesh AND a CC PascalCase mesh.
          const canonical = canonicalForMorph(name);

          const isBlinkShape = blinkShapeNames.includes(name);
          const isEyeShape   = hasEyeSaccades && EYE_SACCADE_SHAPES.includes(canonical);
          const isBrowShape  = BROW_SHAPE_NAMES.includes(canonical);

          // Lip-shape detection — branched between CC and non-CC paths so
          // they never share the same lookup logic. This is the bleed
          // firewall: even if a non-CC rig had a `V_Open`-named morph
          // (it won't, but defensively), the CC branch wouldn't fire on it.
          const isCcLip = isCcRig && isCcBaseLipMorphName(name);
          const isLipShape = isCcLip
            || (!isCcRig
              && ((LIP_SYNC_BLENDSHAPES as ReadonlySet<string>).has(canonical)
                  || /^viseme_/i.test(name)));

          // Compose layers — strongest contributor per shape wins.
          // Beat sits between ambient and procedural so a strong beat
          // expression overrides the calm baseline but blinks/saccades
          // still ride on top.
          const ambientVal = layerWeight(ambientTarget, name, canonical);
          const beatVal    = layerWeight(beatTarget, name, canonical);
          const gestureVal = gestureShapeContribs[name] ?? gestureShapeContribs[canonical] ?? 0;
          let target = Math.max(ambientVal, beatVal);

          // Viseme layer — only on lip shapes, max-merged.
          let visemeVal = 0;
          if (isLipShape) {
            visemeVal = layerWeight(visemeTarget, name, canonical);
            target = Math.max(target, visemeVal);
          }
          if (isBrowShape)  target = Math.max(target, browTwitchValue[canonical] ?? 0);
          if (isBlinkShape) target = Math.max(target, blinkValue);
          if (isEyeShape)   target = Math.max(target, eyeCurrent[canonical] ?? 0);
          // Gesture is the last layer — wins over everything when it's
          // active. Tracking whether it dominates lets us pick the fast
          // gesture lerp constant so transients aren't smeared by the
          // emotion's slow lerp.
          const gestureWins = gestureVal > target;
          if (gestureWins) target = gestureVal;

          // Choose lerp speed — fast for blinks/eyes/visemes, medium for
          // brow twitches & beat emotions, slow for the ambient baseline.
          // Gesture-winning shapes use the fastest lerp so the envelope's
          // attack/decay shape isn't blunted by the per-shape filter.
          const gestureK = 1 - Math.exp(-dtSec / GESTURE_LERP_SEC);
          let frameK = beatK;
          if (gestureWins)             frameK = gestureK;       // ~50 ms
          else if (isLipShape && visemeVal > 0) frameK = visemeK; // viseme tracks audio
          else if (isBlinkShape)       frameK = blinkK;
          else if (isEyeShape)         frameK = blinkK;          // fast: real saccades are 80–200 ms
          else if (isBrowShape)        frameK = browK;
          else if (ambientVal > beatVal) frameK = ambientK;

          influences[i] += (target - influences[i]) * frameK;
        }
      }
    }

    // ── CC jaw bone hinge ────────────────────────────────────────────────
    //
    // Drives `CC_Base_JawRoot` rotation from the canonical `jawOpen`
    // weight in any of the four face-driving layers — ambient / beat /
    // viseme / gesture. The morph alone is a sub-millimetre corrective
    // on this rig, so without this bone hinge the mouth never visibly
    // opens regardless of which layer requested it. `Female_Angled`
    // (the textured face overlay) is skinned to this bone and follows
    // the rotation, which is what the user sees.
    //
    // `expandCcBaseVisemeWeights` preserves the canonical `jawOpen` key
    // on the way through so all three "target dict" layers can supply
    // it. The gesture layer (yawn / gasp / laugh) writes shapes via
    // `gestureShapeContribs`, also keyed by canonical, so we read it
    // here directly — same source the morph mixer above consumes.
    if (ccJawBone && ccJawBindQuat) {
      const ambientJaw = ambientTarget.jawOpen ?? 0;
      const beatJaw = beatTarget.jawOpen ?? 0;
      const visemeJaw = visemeTarget.jawOpen ?? 0;
      const gestureJaw = gestureShapeContribs.jawOpen
        ?? gestureShapeContribs.Jaw_Open
        ?? 0;
      const jawTarget = Math.max(ambientJaw, beatJaw, visemeJaw, gestureJaw);
      const jawK = 1 - Math.exp(-dtSec / VISEME_LERP_SEC);
      ccJawOpenCurrent += (jawTarget - ccJawOpenCurrent) * jawK;
      ccJawDelta.setFromAxisAngle(ccJawAxis, ccJawOpenCurrent * CC_JAW_OPEN_RADIANS);
      (ccJawBone as Object3D).quaternion.copy(ccJawBindQuat).multiply(ccJawDelta);
      // One-shot diag: log first frame jaw target crosses 0.05.
      if (!(globalThis as { __ccJawLogged?: boolean }).__ccJawLogged && jawTarget > 0.05) {
        (globalThis as { __ccJawLogged?: boolean }).__ccJawLogged = true;
        const q = (ccJawBone as Object3D).quaternion;
        const bq = ccJawBindQuat;
        console.log(
          `[ccJaw] target=${jawTarget.toFixed(3)} current=${ccJawOpenCurrent.toFixed(3)} bone=${(ccJawBone as Object3D).name}`
          + ` axis=[${ccJawAxis.x},${ccJawAxis.y},${ccJawAxis.z}]`
          + ` bind=[${bq.x.toFixed(3)},${bq.y.toFixed(3)},${bq.z.toFixed(3)},${bq.w.toFixed(3)}]`
          + ` final=[${q.x.toFixed(3)},${q.y.toFixed(3)},${q.z.toFixed(3)},${q.w.toFixed(3)}]`
          + ` ambient=${ambientJaw.toFixed(3)} beat=${beatJaw.toFixed(3)} viseme=${visemeJaw.toFixed(3)} gesture=${gestureJaw.toFixed(3)}`,
        );
      }
    }
  }

  function setTimeScale(s: number): void {
    // Stores the target only — the actual `currentAction.timeScale` is
    // updated each frame by `update()` lerping `timeScaleCurrent` toward
    // `timeScaleTarget`. This eliminates the "tempo click" the player
    // would otherwise produce on every beat boundary.
    timeScaleTarget = s;
  }

  function setExpression(weights: Record<string, number>): void {
    // Beat-driven layer. Replaced each time a beat starts. Empty weights
    // mean "no beat-specific expression" — the ambient baseline will show.
    // CC rigs additionally need the lip-shape fan-out so a preset like
    // `surprise: { jawOpen: 0.45 }` drives `V_Open` + `V_Lip_Open` +
    // `Jaw_Open` together (Jaw_Open alone barely visibly opens the
    // mouth on CC exports). Non-lip canonicals pass through unchanged.
    beatTarget = isCcRig ? expandCcBaseVisemeWeights(weights) : weights;
  }

  function setAmbient(weights: Record<string, number>): void {
    // Always-on baseline. Should be set once at startup and only changed
    // for global state shifts (e.g. character reload). Survives between beats.
    // Same CC fan-out as `setExpression` so a baseline `mouthSmileLeft`
    // also reaches `V_Wide` etc. on CC rigs.
    ambientTarget = isCcRig ? expandCcBaseVisemeWeights(weights) : weights;
  }

  function setVisemeWeights(weights: Record<string, number>): void {
    // Real-time lip-sync layer from the narration system. Replaced every
    // narration tick. CC rigs need a name fan-out to their V_* visemes +
    // Jaw_Open passthrough — see `cc-face-rig.expandCcBaseVisemeWeights`
    // for the rationale (and the bleed-safety guarantees).
    visemeTarget = isCcRig ? expandCcBaseVisemeWeights(weights) : weights;
  }

  /**
   * Update the emotion-driven eye / brow profile. Called once per beat by
   * the BeatPlayer.
   *
   * - `emotion`  picks an EyeProfile from EYE_PROFILES (falls back to neutral)
   * - `intensity` 0..1 — how strongly to deviate from neutral. Low intensity
   *               blends the chosen profile back toward neutral so a "0.2
   *               anger" beat doesn't read as full glare.
   * - `energy`   0..1 — arousal multiplier on intervals. High energy shrinks
   *               intervals (faster blinks/saccades), low energy stretches
   *               them. Independent of emotion polarity.
   *
   * Schedule timers pick up the new values on their next tick — no resets,
   * no jolts.
   */
  /**
   * Override the procedural saccade target for the current beat. Pass
   * `null` (or omit `direction`) to release the override and let
   * procedural saccades resume.
   *
   * Validates the direction string against the gaze vocabulary; an
   * unknown value is treated as a release so a malformed beat doesn't
   * stick the eyes in a bad position.
   *
   * `intensity` scales the gaze amplitude (defaults to 0.7). A soft
   * gaze (0.4) is for subtle inward processing; a hard gaze (1.0) is
   * for "looks pointedly at the floor".
   */
  function setGazeOverride(direction: string | null | undefined, intensity = 0.7): void {
    if (!hasEyeSaccades) return;
    const validDirs: ReadonlySet<string> = new Set([
      'forward', 'down', 'down_left', 'down_right',
      'up', 'up_left', 'up_right', 'left', 'right', 'away',
    ]);
    if (direction == null || !validDirs.has(direction)) {
      // Release: clear the lock and let the next scheduled saccade run.
      // We zero the eyeTarget so the eyes ease back to centre rather than
      // freezing on the previous override.
      gazeOverride = null;
      gazeOverrideWeights = {};
      eyeTarget = {};
      return;
    }
    gazeOverride = direction as GazeDirection;
    gazeOverrideWeights = gazeWeightsFor(gazeOverride, intensity);
    // Drive eyeTarget immediately so the per-frame lerp pulls toward the
    // override on the next tick. Eye saccade smoothing (~80 ms) means the
    // gaze "snaps" naturally, not as a teleport.
    eyeTarget = gazeOverrideWeights;
  }

  function setEmotionState(emotion: string, intensity = 0.6, energy = 0.5): void {
    const target = getEyeProfileFor(emotion);
    // Lerp profile fields toward neutral by (1 - intensity). At intensity=1
    // we use the target verbatim; at intensity=0 we behave like neutral.
    const t = Math.max(0, Math.min(1, intensity));
    const lerpRange = (a: [number, number], b: [number, number]): [number, number] => [
      a[0] + (b[0] - a[0]) * t,
      a[1] + (b[1] - a[1]) * t,
    ];
    const lerp = (a: number, b: number) => a + (b - a) * t;
    currentEyeProfile = {
      blinkIntervalMs: lerpRange(NEUTRAL_PROFILE.blinkIntervalMs, target.blinkIntervalMs),
      blinkHoldMs:     lerpRange(NEUTRAL_PROFILE.blinkHoldMs,     target.blinkHoldMs),
      saccadeIntervalMs: lerpRange(NEUTRAL_PROFILE.saccadeIntervalMs, target.saccadeIntervalMs),
      saccadeFixationMs: lerpRange(NEUTRAL_PROFILE.saccadeFixationMs, target.saccadeFixationMs),
      saccadeAmpH:     lerpRange(NEUTRAL_PROFILE.saccadeAmpH,     target.saccadeAmpH),
      saccadeAmpV:     lerpRange(NEUTRAL_PROFILE.saccadeAmpV,     target.saccadeAmpV),
      saccadeDownBias: lerp(NEUTRAL_PROFILE.saccadeDownBias, target.saccadeDownBias),
      browIntervalMs:  lerpRange(NEUTRAL_PROFILE.browIntervalMs,  target.browIntervalMs),
      browAmp:         lerpRange(NEUTRAL_PROFILE.browAmp,         target.browAmp),
      // Brow flavour: lerp each weight independently so a low-intensity
      // beat retains some of the neutral mix.
      browFlavour: {
        inner_lift: lerp(NEUTRAL_PROFILE.browFlavour.inner_lift, target.browFlavour.inner_lift),
        furrow:     lerp(NEUTRAL_PROFILE.browFlavour.furrow,     target.browFlavour.furrow),
        outer_lift: lerp(NEUTRAL_PROFILE.browFlavour.outer_lift, target.browFlavour.outer_lift),
        asymmetric: lerp(NEUTRAL_PROFILE.browFlavour.asymmetric, target.browFlavour.asymmetric),
      },
    };
    // Energy → arousal multiplier on intervals. energy 0.0 → 1.5x (slow),
    // energy 0.5 → 1.0x (neutral), energy 1.0 → 0.5x (fast). Clamp so a
    // clamped/extreme energy doesn't degenerate into <100 ms intervals.
    const e = Math.max(0, Math.min(1, energy));
    arousalScale = Math.max(0.45, 1.5 - e);
  }

  return {
    root,
    update,
    setTimeScale,
    playClipFromUrl,
    queueClipFromUrl,
    setOnClipStart,
    setOnClipFinished,
    getClipDuration,
    playAdditiveClipFromUrl,
    setBreathing,
    getBreathingState,
    stop,
    setHold,
    releaseHold,
    releaseAllHolds,
    teardown,
    setExpression,
    setAmbient,
    setVisemeWeights,
    setEmotionState,
    setGazeOverride,
    triggerGesture,
    triggerBodyGesture,
    setBodyState,
    morphTargetNames,
    getCurrentTimeScale: () => timeScaleCurrent,
    getActiveGesture: () => activeGesture,
    getActiveBodyGesture: () => activeBodyGesture,
    getBodyState: () => currentBodyState,
    getActiveProceduralHolds: () => [...proceduralHolds.keys()],
    getHipWorldPosition: (out) => {
      // Hip bone is the right anchor for camera follow because it
      // carries the locomotion translation track while staying clear
      // of head bob (which the camera shouldn't chase). When the bone
      // wasn't discovered (rare — non-Mixamo rig), fall back to root.
      if (hipsBone) {
        (hipsBone as import('three').Object3D).getWorldPosition(out);
      } else {
        root.getWorldPosition(out);
      }
      return out;
    },
    getBindRotations: () => {
      // Shallow copy so callers can't accidentally mutate our snapshot
      // (which would silently invalidate every retarget that follows).
      const out: Partial<Record<BoneSlot, {
        qx: number; qy: number; qz: number; qw: number;
        ex: number; ey: number; ez: number;
      }>> = {};
      for (const slot of Object.keys(bindRotationsBySlot) as BoneSlot[]) {
        const v = bindRotationsBySlot[slot];
        if (v) out[slot] = { ...v };
      }
      return out;
    },
  };
}
